<template>
  <div class="burton-dashboard">
    <section class="hero-card">
      <div class="hero-content">
        <div>
          <h1>Burton 数据看板</h1>
          <p>聚合小程序访问、加购、支付与订单状态，支持今日实时榜与近7天历史趋势。</p>
        </div>
        <div class="hero-actions">
          <el-date-picker v-if="activeTab === 'today'" v-model="queryDate" type="date" value-format="YYYY-MM-DD"
            :clearable="false" :disabled-date="disableFutureDate" placeholder="查询日期" />
          <span class="countdown-text">自动刷新倒计时 {{ refreshCountdownText }}</span>
          <el-popover v-if="activeTab === 'today'" placement="bottom-end" width="270" trigger="click">
            <template #reference>
              <el-button type="primary" plain>选择导出</el-button>
            </template>
            <div class="export-multi-panel">
              <el-checkbox-group v-model="todayExportSelections">
                <el-checkbox label="today-report">Sales Daily Report - TTL</el-checkbox>
                <el-checkbox label="today-summary">今日总榜数据</el-checkbox>
                <el-checkbox label="today-hour">今日小时数据</el-checkbox>
                <el-checkbox label="today-status">今日订单状态分布</el-checkbox>
              </el-checkbox-group>
              <el-button type="primary" size="small" @click="exportTodaySelectedData">导出已勾选</el-button>
            </div>
          </el-popover>
          <el-button type="primary" :loading="loading" @click="manualRefresh">刷新看板</el-button>
        </div>
      </div>
      <div class="hero-meta">
        <span v-if="activeTab === 'today'">查询日期：{{ queryDate || '-' }}</span>
        <span>项目上线日期：{{ launchDate || '-' }}</span>
        <span>历史窗口：{{ historyWindowText }}</span>
        <span>最近刷新：{{ lastRefreshTime || '-' }}</span>
      </div>
    </section>

    <el-tabs v-model="activeTab" class="dashboard-tabs">
      <el-tab-pane label="今日总榜 + 小时榜" name="today" lazy>
        <section class="kpi-grid" v-loading="loading">
          <article v-for="item in todayKpiCards" :key="item.key" class="kpi-card">
            <div class="kpi-label">
              <span>{{ item.label }}</span>
              <el-tooltip :content="item.desc" placement="top">
                <el-icon class="metric-help">
                  <QuestionFilled />
                </el-icon>
              </el-tooltip>
            </div>
            <p class="kpi-value">{{ item.value }}</p>
            <p v-if="item.mom !== null && item.mom !== undefined" class="kpi-mom" :class="momClass(item.mom)">
              {{ momArrow(item.mom) }} 较昨日 {{ formatMomRate(item.mom) }}
            </p>
            <p class="kpi-desc">{{ item.desc }}</p>
          </article>
        </section>

        <section class="data-grid">
          <el-card class="panel-card" shadow="hover" v-loading="loading && activeTab === 'today'">
            <template #header>
              <div class="panel-header">
                <span>今日小时榜</span>
                <div class="panel-actions">
                  <span class="panel-tip">按小时聚合，范围为今日00:00至次日00:00</span>
                </div>
              </div>
            </template>
            <el-table ref="hourTableRef" class="hourly-table" :data="todayHourlyRows" stripe height="430"
              :row-class-name="getHourRowClass">
              <el-table-column prop="bucket" label="小时" width="120" />
              <el-table-column prop="visitPv" min-width="110">
                <template #header>
                  <span class="metric-head">访问次数 <el-tooltip :content="METRIC_DESC.visitPv" placement="top"><el-icon
                        class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.visitPv) }}</span>
                    <span class="mom-text" :class="momClass(row.visitPvMom)">{{ momArrow(row.visitPvMom) }} {{
                      formatMomRate(row.visitPvMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="visitUv" min-width="110">
                <template #header>
                  <span class="metric-head">访问人数 <el-tooltip :content="METRIC_DESC.visitUv" placement="top"><el-icon
                        class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.visitUv) }}</span>
                    <span class="mom-text" :class="momClass(row.visitUvMom)">{{ momArrow(row.visitUvMom) }} {{
                      formatMomRate(row.visitUvMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="addUv" min-width="110">
                <template #header>
                  <span class="metric-head">加购人数 <el-tooltip :content="METRIC_DESC.addUv" placement="top"><el-icon
                        class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.addUv) }}</span>
                    <span class="mom-text" :class="momClass(row.addUvMom)">{{ momArrow(row.addUvMom) }} {{
                      formatMomRate(row.addUvMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="totalOrderCnt" min-width="110">
                <template #header>
                  <span class="metric-head">总订单数 <el-tooltip :content="METRIC_DESC.totalOrderCnt"
                      placement="top"><el-icon class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.totalOrderCnt) }}</span>
                    <span class="mom-text" :class="momClass(row.totalOrderCntMom)">{{ momArrow(row.totalOrderCntMom) }}
                      {{ formatMomRate(row.totalOrderCntMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="orderUserCnt" min-width="120">
                <template #header>
                  <span class="metric-head">下单人数 <el-tooltip :content="METRIC_DESC.orderUserCnt"
                      placement="top"><el-icon class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.orderUserCnt) }}</span>
                    <span class="mom-text" :class="momClass(row.orderUserCntMom)">{{ momArrow(row.orderUserCntMom) }} {{
                      formatMomRate(row.orderUserCntMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="paidOrderCnt" min-width="120">
                <template #header>
                  <span class="metric-head">支付订单数 <el-tooltip :content="METRIC_DESC.paidOrderCnt"
                      placement="top"><el-icon class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.paidOrderCnt) }}</span>
                    <span class="mom-text" :class="momClass(row.paidOrderCntMom)">{{ momArrow(row.paidOrderCntMom) }} {{
                      formatMomRate(row.paidOrderCntMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="orderUnpaidUserCnt" min-width="140">
                <template #header>
                  <span class="metric-head">订单未支付人数 <el-tooltip :content="METRIC_DESC.orderUnpaidUserCnt"
                      placement="top"><el-icon class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.orderUnpaidUserCnt) }}</span>
                    <span class="mom-text" :class="momClass(row.orderUnpaidUserCntMom)">{{
                      momArrow(row.orderUnpaidUserCntMom) }} {{ formatMomRate(row.orderUnpaidUserCntMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="payUserCnt" min-width="110">
                <template #header>
                  <span class="metric-head">支付人数 <el-tooltip :content="METRIC_DESC.payUserCnt" placement="top"><el-icon
                        class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.payUserCnt) }}</span>
                    <span class="mom-text" :class="momClass(row.payUserMom)">{{ momArrow(row.payUserMom) }} {{
                      formatMomRate(row.payUserMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="waitPayUserCnt" min-width="120">
                <template #header>
                  <span class="metric-head">待支付人数 <el-tooltip :content="METRIC_DESC.waitPayUserCnt"
                      placement="top"><el-icon class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.waitPayUserCnt) }}</span>
                    <span class="mom-text" :class="momClass(row.waitPayUserCntMom)">{{ momArrow(row.waitPayUserCntMom)
                      }} {{ formatMomRate(row.waitPayUserCntMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column min-width="110">
                <template #header>
                  <span class="metric-head">下单转化率 <el-tooltip :content="METRIC_DESC.orderRate" placement="top"><el-icon
                        class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatPercent(calcRate(row.totalOrderCnt, row.visitUv)) }}</span>
                    <span class="mom-text" :class="momClass(row.orderRateMom)">{{ momArrow(row.orderRateMom) }} {{
                      formatMomRate(row.orderRateMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column min-width="120">
                <template #header>
                  <span class="metric-head">订单流失率 <el-tooltip :content="METRIC_DESC.orderLossRate"
                      placement="top"><el-icon class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatPercent(calcRate(row.orderUnpaidUserCnt, row.totalOrderCnt)) }}</span>
                    <span class="mom-text" :class="momClass(row.orderLossRateMom)">{{ momArrow(row.orderLossRateMom) }}
                      {{ formatMomRate(row.orderLossRateMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column min-width="110">
                <template #header>
                  <span class="metric-head">支付转化率 <el-tooltip :content="METRIC_DESC.payRate" placement="top"><el-icon
                        class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatPercent(calcRate(row.payUserCnt, row.visitUv)) }}</span>
                    <span class="mom-text" :class="momClass(row.payRateMom)">{{ momArrow(row.payRateMom) }} {{
                      formatMomRate(row.payRateMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="orderAmount" min-width="120">
                <template #header>
                  <span class="metric-head">下单金额 <el-tooltip :content="METRIC_DESC.orderAmount" placement="top"><el-icon
                        class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatAmount(row.orderAmount) }}</span>
                    <span class="mom-text" :class="momClass(row.orderAmountMom)">{{ momArrow(row.orderAmountMom) }} {{
                      formatMomRate(row.orderAmountMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="orderItemQty" min-width="120">
                <template #header>
                  <span class="metric-head">下单总件数 <el-tooltip :content="METRIC_DESC.orderItemQty"
                      placement="top"><el-icon class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.orderItemQty) }}</span>
                    <span class="mom-text" :class="momClass(row.orderItemQtyMom)">{{ momArrow(row.orderItemQtyMom) }} {{
                      formatMomRate(row.orderItemQtyMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="avgOrderAmount" min-width="130">
                <template #header>
                  <span class="metric-head">下单平均金额</span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatAmount(row.avgOrderAmount) }}</span>
                    <span class="mom-text" :class="momClass(row.avgOrderAmountMom)">{{ momArrow(row.avgOrderAmountMom)
                      }} {{ formatMomRate(row.avgOrderAmountMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="payAmount" min-width="120">
                <template #header>
                  <span class="metric-head">支付金额 <el-tooltip :content="METRIC_DESC.payAmount" placement="top"><el-icon
                        class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatAmount(row.payAmount) }}</span>
                    <span class="mom-text" :class="momClass(row.payAmountMom)">{{ momArrow(row.payAmountMom) }} {{
                      formatMomRate(row.payAmountMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="paidItemQty" min-width="120">
                <template #header>
                  <span class="metric-head">支付总件数 <el-tooltip :content="METRIC_DESC.paidItemQty"
                      placement="top"><el-icon class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatNumber(row.paidItemQty) }}</span>
                    <span class="mom-text" :class="momClass(row.paidItemQtyMom)">{{ momArrow(row.paidItemQtyMom) }} {{
                      formatMomRate(row.paidItemQtyMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="avgPayAmount" min-width="130">
                <template #header>
                  <span class="metric-head">支付平均金额</span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatAmount(row.avgPayAmount) }}</span>
                    <span class="mom-text" :class="momClass(row.avgPayAmountMom)">{{ momArrow(row.avgPayAmountMom) }} {{
                      formatMomRate(row.avgPayAmountMom) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column min-width="120">
                <template #header>
                  <span class="metric-head">待支付流失率 <el-tooltip :content="METRIC_DESC.waitPayLossRate"
                      placement="top"><el-icon class="metric-help">
                        <QuestionFilled />
                      </el-icon></el-tooltip></span>
                </template>
                <template #default="{ row }">
                  <div class="metric-with-mom">
                    <span>{{ formatPercent(calcRate(row.waitPayUserCnt, row.payUserCnt + row.waitPayUserCnt)) }}</span>
                    <span class="mom-text" :class="momClass(row.waitPayLossRateMom)">{{ momArrow(row.waitPayLossRateMom)
                      }} {{ formatMomRate(row.waitPayLossRateMom) }}</span>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </el-card>

          <el-card class="panel-card" shadow="hover" v-loading="loading && activeTab === 'today'">
            <template #header>
              <div class="panel-header">
                <span>今日订单状态分布</span>
                <span class="panel-tip">订单创建时间口径</span>
              </div>
            </template>
            <el-table :data="todayOrderStatusRows" stripe height="430">
              <el-table-column prop="statusLabel" label="订单状态" min-width="180" />
              <el-table-column prop="status" label="状态码" min-width="220" />
              <el-table-column prop="count" label="订单数" min-width="100">
                <template #default="{ row }">{{ formatNumber(row.count) }}</template>
              </el-table-column>
              <el-table-column label="占比" min-width="120">
                <template #default="{ row }">{{ formatPercent(calcRate(row.count, todayOverview.orderCnt)) }}</template>
              </el-table-column>
            </el-table>
          </el-card>
        </section>

        <section class="chart-grid">
          <el-card class="panel-card" shadow="hover">
            <template #header>
              <div class="panel-header">
                <span>今日小时趋势图</span>
                <el-select v-model="todayTrendMetric" size="small" class="trend-select">
                  <el-option v-for="item in trendMetricOptions" :key="item.value" :label="item.label"
                    :value="item.value" />
                </el-select>
              </div>
            </template>
            <div ref="todayTrendChartRef" class="echart-box"></div>
          </el-card>
          <el-card class="panel-card" shadow="hover">
            <template #header>
              <div class="panel-header">
                <span>今日订单状态分布图</span>
              </div>
            </template>
            <div ref="todayStatusChartRef" class="echart-box"></div>
          </el-card>
        </section>
      </el-tab-pane>

      <el-tab-pane label="历史按天（近7天）" name="history" lazy>
        <el-card class="panel-card" shadow="hover">
          <div class="panel-header">
            <span>历史查询条件</span>
            <div class="panel-actions">
              <el-date-picker v-model="historyRange" type="daterange" value-format="YYYY-MM-DD" :clearable="false"
                unlink-panels start-placeholder="开始日期" end-placeholder="结束日期" :disabled-date="disableHistoryDate" />
              <el-button type="primary" size="small" @click="applyHistoryRangeReload">查询</el-button>
              <span class="panel-tip">默认近7天，可自定义时间</span>
              <span class="panel-tip">区间规则：开始时间含当天00:00:00，结束时间取所选日期次日00:00:00（不含）</span>
              <span class="panel-tip">导出默认导出当前页面时间段数据</span>
              <el-button class="export-btn" type="primary" plain size="small" @click="exportDayData">
                <el-icon>
                  <Download />
                </el-icon>
                导出历史数据
              </el-button>
            </div>
          </div>
        </el-card>

        <section class="kpi-grid" v-loading="loading">
          <article v-for="item in historyKpiCards" :key="item.key" class="kpi-card">
            <div class="kpi-label">
              <span>{{ item.label }}</span>
              <el-tooltip :content="item.desc" placement="top">
                <el-icon class="metric-help">
                  <QuestionFilled />
                </el-icon>
              </el-tooltip>
            </div>
            <p class="kpi-value">{{ item.value }}</p>
            <p class="kpi-desc">{{ item.desc }}</p>
          </article>
        </section>

        <el-card class="panel-card" shadow="hover" v-loading="loading && activeTab === 'history'">
          <template #header>
            <div class="panel-header">
              <span>历史日榜</span>
              <span class="panel-tip">按天统计明细</span>
            </div>
          </template>
          <el-empty v-if="!historyRows.length && !loading" description="当前历史区间暂无数据，可调整项目上线日期后刷新" />
          <el-table v-else :data="historyRows" stripe height="430">
            <el-table-column prop="bucket" label="日期" width="130" />
            <el-table-column prop="visitPv" min-width="100">
              <template #header>
                <span class="metric-head">访问次数 <el-tooltip :content="METRIC_DESC.visitPv" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.visitPv) }}</span>
                  <span class="mom-text" :class="momClass(row.visitPvMom)">{{ momArrow(row.visitPvMom) }} {{
                    formatMomRate(row.visitPvMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="visitUv" min-width="100">
              <template #header>
                <span class="metric-head">访问人数 <el-tooltip :content="METRIC_DESC.visitUv" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.visitUv) }}</span>
                  <span class="mom-text" :class="momClass(row.visitUvMom)">{{ momArrow(row.visitUvMom) }} {{
                    formatMomRate(row.visitUvMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="addUv" min-width="100">
              <template #header>
                <span class="metric-head">加购人数 <el-tooltip :content="METRIC_DESC.addUv" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.addUv) }}</span>
                  <span class="mom-text" :class="momClass(row.addUvMom)">{{ momArrow(row.addUvMom) }} {{
                    formatMomRate(row.addUvMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="totalOrderCnt" min-width="100">
              <template #header>
                <span class="metric-head">总订单数 <el-tooltip :content="METRIC_DESC.totalOrderCnt" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.totalOrderCnt) }}</span>
                  <span class="mom-text" :class="momClass(row.totalOrderCntMom)">{{ momArrow(row.totalOrderCntMom) }} {{
                    formatMomRate(row.totalOrderCntMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="orderUserCnt" min-width="110">
              <template #header>
                <span class="metric-head">下单人数 <el-tooltip :content="METRIC_DESC.orderUserCnt" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.orderUserCnt) }}</span>
                  <span class="mom-text" :class="momClass(row.orderUserCntMom)">{{ momArrow(row.orderUserCntMom) }} {{
                    formatMomRate(row.orderUserCntMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="paidOrderCnt" min-width="110">
              <template #header>
                <span class="metric-head">支付订单数 <el-tooltip :content="METRIC_DESC.paidOrderCnt" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.paidOrderCnt) }}</span>
                  <span class="mom-text" :class="momClass(row.paidOrderCntMom)">{{ momArrow(row.paidOrderCntMom) }} {{
                    formatMomRate(row.paidOrderCntMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="orderUnpaidUserCnt" min-width="130">
              <template #header>
                <span class="metric-head">订单未支付人数 <el-tooltip :content="METRIC_DESC.orderUnpaidUserCnt"
                    placement="top"><el-icon class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.orderUnpaidUserCnt) }}</span>
                  <span class="mom-text" :class="momClass(row.orderUnpaidUserCntMom)">{{
                    momArrow(row.orderUnpaidUserCntMom) }} {{ formatMomRate(row.orderUnpaidUserCntMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="payUserCnt" min-width="100">
              <template #header>
                <span class="metric-head">支付人数 <el-tooltip :content="METRIC_DESC.payUserCnt" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.payUserCnt) }}</span>
                  <span class="mom-text" :class="momClass(row.payUserMom)">{{ momArrow(row.payUserMom) }} {{
                    formatMomRate(row.payUserMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="waitPayUserCnt" min-width="110">
              <template #header>
                <span class="metric-head">待支付人数 <el-tooltip :content="METRIC_DESC.waitPayUserCnt"
                    placement="top"><el-icon class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.waitPayUserCnt) }}</span>
                  <span class="mom-text" :class="momClass(row.waitPayUserCntMom)">{{ momArrow(row.waitPayUserCntMom) }}
                    {{ formatMomRate(row.waitPayUserCntMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column min-width="110">
              <template #header>
                <span class="metric-head">下单转化率 <el-tooltip :content="METRIC_DESC.orderRate" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatPercent(calcRate(row.totalOrderCnt, row.visitUv)) }}</span>
                  <span class="mom-text" :class="momClass(row.orderRateMom)">{{ momArrow(row.orderRateMom) }} {{
                    formatMomRate(row.orderRateMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column min-width="120">
              <template #header>
                <span class="metric-head">订单流失率 <el-tooltip :content="METRIC_DESC.orderLossRate"
                    placement="top"><el-icon class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatPercent(calcRate(row.orderUnpaidUserCnt, row.totalOrderCnt)) }}</span>
                  <span class="mom-text" :class="momClass(row.orderLossRateMom)">{{ momArrow(row.orderLossRateMom) }} {{
                    formatMomRate(row.orderLossRateMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column min-width="110">
              <template #header>
                <span class="metric-head">支付转化率 <el-tooltip :content="METRIC_DESC.payRate" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatPercent(calcRate(row.payUserCnt, row.visitUv)) }}</span>
                  <span class="mom-text" :class="momClass(row.payRateMom)">{{ momArrow(row.payRateMom) }} {{
                    formatMomRate(row.payRateMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="orderAmount" min-width="120">
              <template #header>
                <span class="metric-head">下单金额 <el-tooltip :content="METRIC_DESC.orderAmount" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatAmount(row.orderAmount) }}</span>
                  <span class="mom-text" :class="momClass(row.orderAmountMom)">{{ momArrow(row.orderAmountMom) }} {{
                    formatMomRate(row.orderAmountMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="orderItemQty" min-width="120">
              <template #header>
                <span class="metric-head">下单总件数 <el-tooltip :content="METRIC_DESC.orderItemQty" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.orderItemQty) }}</span>
                  <span class="mom-text" :class="momClass(row.orderItemQtyMom)">{{ momArrow(row.orderItemQtyMom) }} {{
                    formatMomRate(row.orderItemQtyMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="avgOrderAmount" min-width="130">
              <template #header>
                <span class="metric-head">下单平均金额</span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatAmount(row.avgOrderAmount) }}</span>
                  <span class="mom-text" :class="momClass(row.avgOrderAmountMom)">{{ momArrow(row.avgOrderAmountMom) }}
                    {{ formatMomRate(row.avgOrderAmountMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="payAmount" min-width="120">
              <template #header>
                <span class="metric-head">支付金额 <el-tooltip :content="METRIC_DESC.payAmount" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatAmount(row.payAmount) }}</span>
                  <span class="mom-text" :class="momClass(row.payAmountMom)">{{ momArrow(row.payAmountMom) }} {{
                    formatMomRate(row.payAmountMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="paidItemQty" min-width="120">
              <template #header>
                <span class="metric-head">支付总件数 <el-tooltip :content="METRIC_DESC.paidItemQty" placement="top"><el-icon
                      class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatNumber(row.paidItemQty) }}</span>
                  <span class="mom-text" :class="momClass(row.paidItemQtyMom)">{{ momArrow(row.paidItemQtyMom) }} {{
                    formatMomRate(row.paidItemQtyMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="avgPayAmount" min-width="130">
              <template #header>
                <span class="metric-head">支付平均金额</span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatAmount(row.avgPayAmount) }}</span>
                  <span class="mom-text" :class="momClass(row.avgPayAmountMom)">{{ momArrow(row.avgPayAmountMom) }} {{
                    formatMomRate(row.avgPayAmountMom) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column min-width="120">
              <template #header>
                <span class="metric-head">待支付流失率 <el-tooltip :content="METRIC_DESC.waitPayLossRate"
                    placement="top"><el-icon class="metric-help">
                      <QuestionFilled />
                    </el-icon></el-tooltip></span>
              </template>
              <template #default="{ row }">
                <div class="metric-with-mom">
                  <span>{{ formatPercent(calcRate(row.waitPayUserCnt, row.payUserCnt + row.waitPayUserCnt)) }}</span>
                  <span class="mom-text" :class="momClass(row.waitPayLossRateMom)">{{ momArrow(row.waitPayLossRateMom)
                    }} {{ formatMomRate(row.waitPayLossRateMom) }}</span>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
        <el-card class="panel-card history-trend-card" shadow="hover">
          <template #header>
            <div class="panel-header">
              <span>历史日趋势图</span>
              <el-select v-model="historyTrendMetric" size="small" class="trend-select">
                <el-option v-for="item in trendMetricOptions" :key="item.value" :label="item.label"
                  :value="item.value" />
              </el-select>
            </div>
          </template>
          <div ref="historyTrendChartRef" class="echart-box"></div>
        </el-card>
      </el-tab-pane>

      <el-tab-pane label="员工账号看板" name="employee" lazy>
        <el-card class="panel-card" shadow="hover" v-loading="employeeLoading">
          <template #header>
            <div class="panel-header">
              <span>活动与汇总</span>
              <div class="panel-actions">
                <el-select v-model="selectedEmployeeActivityId" class="employee-activity-select" filterable
                  placeholder="请选择活动" @change="onEmployeeActivityChange">
                  <el-option v-for="item in employeeActivities" :key="item.id"
                    :label="`${item.name}（${item.statusText}）`" :value="item.id" />
                </el-select>
                <el-popover placement="bottom-end" width="300" trigger="click">
                  <template #reference>
                    <el-button type="primary" plain size="small">选择导出</el-button>
                  </template>
                  <div class="export-multi-panel">
                    <el-checkbox-group v-model="employeeExportSelections">
                      <el-checkbox label="friend-report">累计账号购买情况导出</el-checkbox>
                      <el-checkbox label="summary">汇总统计</el-checkbox>
                      <el-checkbox label="employee-list">员工账号列表</el-checkbox>
                      <el-checkbox label="friend-list">亲友账号列表</el-checkbox>
                      <el-checkbox label="employee-rank">员工下单排行榜</el-checkbox>
                      <el-checkbox label="friend-rank">亲友下单排行榜</el-checkbox>
                      <el-checkbox label="combined-rank">员工及亲友排行榜</el-checkbox>
                    </el-checkbox-group>
                    <el-button type="primary" size="small" @click="exportEmployeeDashboardData">导出已勾选</el-button>
                  </div>
                </el-popover>
              </div>
            </div>
          </template>
          <div v-if="selectedEmployeeActivity" class="employee-activity-meta">
            <span>活动：{{ selectedEmployeeActivity.name }}</span>
            <span>状态：{{ selectedEmployeeActivity.statusText }}</span>
            <span>开始时间：{{ formatDisplayDateTime(selectedEmployeeActivity.start_time) }}</span>
            <span>结束时间：{{ formatDisplayDateTime(selectedEmployeeActivity.end_time) }}</span>
          </div>
          <el-empty v-else description="暂无员工活动数据" />
          <section v-if="selectedEmployeeActivity" class="employee-summary-grid">
            <article class="kpi-card">
              <p class="kpi-label">员工账号数量</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.employeeAccountCnt) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工账号在用</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.employeeAccountUsedCnt) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工账号未用</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.employeeAccountUnusedCnt) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友账号数量</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.friendAccountCnt) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友账号在用</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.friendAccountUsedCnt) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友账号未用</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.friendAccountUnusedCnt) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工下单总订单数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.employeeOrderCntTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友下单总订单数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.friendOrderCntTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工及亲友下单总订单数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.allOrderCntTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工支付总订单数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.employeePaidOrderCntTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友支付总订单数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.friendPaidOrderCntTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工及亲友支付总订单数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.allPaidOrderCntTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工下单总金额</p>
              <p class="kpi-value">{{ formatAmount(employeeSummary.employeeOrderAmountTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友下单总金额</p>
              <p class="kpi-value">{{ formatAmount(employeeSummary.friendOrderAmountTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工及亲友下单总金额</p>
              <p class="kpi-value">{{ formatAmount(employeeSummary.allOrderAmountTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工支付总金额</p>
              <p class="kpi-value">{{ formatAmount(employeeSummary.employeePayAmountTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友支付总金额</p>
              <p class="kpi-value">{{ formatAmount(employeeSummary.friendPayAmountTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工及亲友支付总金额</p>
              <p class="kpi-value">{{ formatAmount(employeeSummary.allPayAmountTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工下单总件数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.employeeOrderItemQtyTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友下单总件数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.friendOrderItemQtyTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工及亲友下单总件数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.allOrderItemQtyTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工支付总件数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.employeePaidItemQtyTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友支付总件数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.friendPaidItemQtyTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工及亲友支付总件数</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.allPaidItemQtyTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工额度已用总额</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.employeeUsedQuotaTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">亲友额度已用总额</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.friendUsedQuotaTotal) }}</p>
            </article>
            <article class="kpi-card">
              <p class="kpi-label">员工及亲友额度已用总额</p>
              <p class="kpi-value">{{ formatNumber(employeeSummary.allUsedQuotaTotal) }}</p>
            </article>
          </section>
        </el-card>

        <section class="employee-rank-grid">
          <el-card class="panel-card" shadow="hover" v-loading="employeeLoading">
            <template #header>
              <div class="panel-header">
                <span>员工个人下单排行榜</span>
                <el-select v-model="employeeRankSortMetric" size="small" class="rank-select">
                  <el-option v-for="item in employeeRankMetricOptions" :key="item.value" :label="item.label"
                    :value="item.value" />
                </el-select>
              </div>
            </template>
            <el-empty v-if="!sortedEmployeeOrderRankRows.length && !employeeLoading" description="暂无下单数据" />
            <el-table v-else :data="sortedEmployeeOrderRankRows" stripe height="320">
              <el-table-column type="index" label="排名" width="70" />
              <el-table-column prop="employeeName" label="员工名称" min-width="120" />
              <el-table-column prop="employeeNumber" label="员工号" min-width="120" />
              <el-table-column prop="groupName" label="分组名称" min-width="120" />
              <el-table-column prop="orderCnt" label="下单总订单数" min-width="120">
                <template #default="{ row }">{{ formatNumber(row.orderCnt) }}</template>
              </el-table-column>
              <el-table-column prop="paidOrderCnt" label="支付总订单数" min-width="120">
                <template #default="{ row }">{{ formatNumber(row.paidOrderCnt) }}</template>
              </el-table-column>
              <el-table-column prop="orderAmount" label="下单总金额" min-width="120">
                <template #default="{ row }">{{ formatAmount(row.orderAmount) }}</template>
              </el-table-column>
              <el-table-column prop="payAmount" label="支付总金额" min-width="120">
                <template #default="{ row }">{{ formatAmount(row.payAmount) }}</template>
              </el-table-column>
              <el-table-column prop="employeeUsedQuotaTotal" label="员工额度已用总额" min-width="130">
                <template #default="{ row }">{{ formatNumber(row.employeeUsedQuotaTotal) }}</template>
              </el-table-column>
              <el-table-column prop="friendUsedQuotaTotal" label="亲友额度已用总额" min-width="130">
                <template #default="{ row }">{{ formatNumber(row.friendUsedQuotaTotal) }}</template>
              </el-table-column>
              <el-table-column prop="allUsedQuotaTotal" label="员工+亲友额度已用总额" min-width="150">
                <template #default="{ row }">{{ formatNumber(row.allUsedQuotaTotal) }}</template>
              </el-table-column>
              <el-table-column prop="orderItemQty" label="下单总件数" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.orderItemQty) }}</template>
              </el-table-column>
              <el-table-column prop="paidItemQty" label="支付总件数" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.paidItemQty) }}</template>
              </el-table-column>
            </el-table>
          </el-card>

          <el-card class="panel-card" shadow="hover" v-loading="employeeLoading">
            <template #header>
              <div class="panel-header">
                <span>亲友下单排行榜</span>
                <el-select v-model="friendRankSortMetric" size="small" class="rank-select">
                  <el-option v-for="item in friendRankMetricOptions" :key="item.value" :label="item.label"
                    :value="item.value" />
                </el-select>
              </div>
            </template>
            <el-empty v-if="!sortedFriendOrderRankRows.length && !employeeLoading" description="暂无下单数据" />
            <el-table v-else :data="sortedFriendOrderRankRows" stripe height="320">
              <el-table-column type="index" label="排名" width="70" />
              <el-table-column prop="friendName" label="亲友名称" min-width="120" />
              <el-table-column prop="friendEmployeeNumber" label="亲友员工号" min-width="120" />
              <el-table-column prop="ownerEmployeeName" label="归属员工" min-width="120" />
              <el-table-column prop="orderCnt" label="下单总订单数" min-width="120">
                <template #default="{ row }">{{ formatNumber(row.orderCnt) }}</template>
              </el-table-column>
              <el-table-column prop="paidOrderCnt" label="支付总订单数" min-width="120">
                <template #default="{ row }">{{ formatNumber(row.paidOrderCnt) }}</template>
              </el-table-column>
              <el-table-column prop="orderAmount" label="下单总金额" min-width="120">
                <template #default="{ row }">{{ formatAmount(row.orderAmount) }}</template>
              </el-table-column>
              <el-table-column prop="payAmount" label="支付总金额" min-width="120">
                <template #default="{ row }">{{ formatAmount(row.payAmount) }}</template>
              </el-table-column>
              <el-table-column prop="employeeUsedQuotaTotal" label="员工额度已用总额" min-width="130">
                <template #default="{ row }">{{ formatNumber(row.employeeUsedQuotaTotal) }}</template>
              </el-table-column>
              <el-table-column prop="friendUsedQuotaTotal" label="亲友额度已用总额" min-width="130">
                <template #default="{ row }">{{ formatNumber(row.friendUsedQuotaTotal) }}</template>
              </el-table-column>
              <el-table-column prop="allUsedQuotaTotal" label="员工+亲友额度已用总额" min-width="150">
                <template #default="{ row }">{{ formatNumber(row.allUsedQuotaTotal) }}</template>
              </el-table-column>
              <el-table-column prop="orderItemQty" label="下单总件数" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.orderItemQty) }}</template>
              </el-table-column>
              <el-table-column prop="paidItemQty" label="支付总件数" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.paidItemQty) }}</template>
              </el-table-column>
            </el-table>
          </el-card>

          <el-card class="panel-card" shadow="hover" v-loading="employeeLoading">
            <template #header>
              <div class="panel-header">
                <span>员工及亲友下单排行榜</span>
                <el-select v-model="combinedRankSortMetric" size="small" class="rank-select">
                  <el-option v-for="item in combinedRankMetricOptions" :key="item.value" :label="item.label"
                    :value="item.value" />
                </el-select>
              </div>
            </template>
            <el-empty v-if="!sortedCombinedOrderRankRows.length && !employeeLoading" description="暂无下单数据" />
            <el-table v-else :data="sortedCombinedOrderRankRows" stripe height="320">
              <el-table-column type="index" label="排名" width="70" />
              <el-table-column prop="employeeName" label="员工名称" min-width="120" />
              <el-table-column prop="employeeNumber" label="员工号" min-width="120" />
              <el-table-column prop="employeeOrderCnt" label="员工下单总订单数" min-width="140">
                <template #default="{ row }">{{ formatNumber(row.employeeOrderCnt) }}</template>
              </el-table-column>
              <el-table-column prop="friendOrderCnt" label="亲友下单总订单数" min-width="140">
                <template #default="{ row }">{{ formatNumber(row.friendOrderCnt) }}</template>
              </el-table-column>
              <el-table-column prop="totalOrderCnt" label="下单总订单数" min-width="120">
                <template #default="{ row }">{{ formatNumber(row.totalOrderCnt) }}</template>
              </el-table-column>
              <el-table-column prop="paidOrderCnt" label="支付总订单数" min-width="120">
                <template #default="{ row }">{{ formatNumber(row.paidOrderCnt) }}</template>
              </el-table-column>
              <el-table-column prop="orderAmount" label="下单总金额" min-width="120">
                <template #default="{ row }">{{ formatAmount(row.orderAmount) }}</template>
              </el-table-column>
              <el-table-column prop="payAmount" label="支付总金额" min-width="120">
                <template #default="{ row }">{{ formatAmount(row.payAmount) }}</template>
              </el-table-column>
              <el-table-column prop="friendUsedQuotaTotal" label="亲友额度已用总额" min-width="130">
                <template #default="{ row }">{{ formatNumber(row.friendUsedQuotaTotal) }}</template>
              </el-table-column>
              <el-table-column prop="allUsedQuotaTotal" label="员工+亲友额度已用总额" min-width="150">
                <template #default="{ row }">{{ formatNumber(row.allUsedQuotaTotal) }}</template>
              </el-table-column>
              <el-table-column prop="orderItemQty" label="下单总件数" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.orderItemQty) }}</template>
              </el-table-column>
              <el-table-column prop="paidItemQty" label="支付总件数" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.paidItemQty) }}</template>
              </el-table-column>
            </el-table>
          </el-card>
        </section>

        <el-card class="panel-card employee-list-card" shadow="hover" v-loading="employeeLoading">
          <template #header>
            <div class="panel-header">
              <span>员工账号列表</span>
              <div class="panel-actions">
                <el-input v-model="employeeSearchForm.employeeName" placeholder="员工名称" clearable class="search-input" />
                <el-input v-model="employeeSearchForm.groupName" placeholder="分组名称" clearable class="search-input" />
                <el-select v-model="employeeSearchForm.employeeAccountStatus" placeholder="员工账号状态" clearable
                  class="search-select">
                  <el-option label="在用" value="used" />
                  <el-option label="未用" value="unused" />
                </el-select>
                <el-select v-model="employeeSearchForm.friendAccountStatus" placeholder="亲友账号状态" clearable
                  class="search-select">
                  <el-option label="在用" value="used" />
                  <el-option label="未用" value="unused" />
                </el-select>
                <el-button type="primary" size="small" @click="queryEmployeeAccounts">查询</el-button>
                <el-button size="small" @click="resetEmployeeSearch">重置</el-button>
              </div>
            </div>
          </template>

          <el-table :data="employeeAccountRows" stripe height="460" row-key="employeeAccountId"
            @expand-change="handleEmployeeExpand">
            <el-table-column type="expand" width="54">
              <template #default="{ row }">
                <div class="friend-table-wrap" v-loading="row.friendLoading">
                  <el-empty v-if="!row.friendRows?.length && !row.friendLoading" description="该员工账号暂无亲友账号" />
                  <el-table v-else :data="row.friendRows" stripe size="small">
                    <el-table-column prop="friendAccountId" label="亲友账号ID" min-width="120" />
                    <el-table-column prop="friendName" label="亲友姓名" min-width="120" />
                    <el-table-column prop="friendEmployeeNumber" label="亲友员工号" min-width="120" />
                    <el-table-column prop="friendStatusText" label="账号状态" min-width="90" />
                    <el-table-column prop="usedQuota" label="使用额度" min-width="100">
                      <template #default="{ row: friend }">{{ formatNumber(friend.usedQuota) }}</template>
                    </el-table-column>
                    <el-table-column prop="remainingQuota" label="剩余额度" min-width="100">
                      <template #default="{ row: friend }">{{ formatNumber(friend.remainingQuota) }}</template>
                    </el-table-column>
                    <el-table-column prop="orderCnt" label="下单数量" min-width="100">
                      <template #default="{ row: friend }">{{ formatNumber(friend.orderCnt) }}</template>
                    </el-table-column>
                  </el-table>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="employeeName" label="员工名称" min-width="120" />
            <el-table-column prop="groupName" label="分组名称" min-width="120" />
            <el-table-column prop="employeeNumber" label="员工号" min-width="120" />
            <el-table-column prop="departmentName" label="部门" min-width="120" />
            <el-table-column prop="accountStatusText" label="员工账号状态" min-width="110" />
            <el-table-column prop="usedQuota" label="使用额度" min-width="100">
              <template #default="{ row }">{{ formatNumber(row.usedQuota) }}</template>
            </el-table-column>
            <el-table-column prop="remainingQuota" label="剩余额度" min-width="100">
              <template #default="{ row }">{{ formatNumber(row.remainingQuota) }}</template>
            </el-table-column>
            <el-table-column prop="friendUsedQuotaTotal" label="亲友已用额度总额" min-width="130">
              <template #default="{ row }">{{ formatNumber(row.friendUsedQuotaTotal) }}</template>
            </el-table-column>
            <el-table-column prop="allUsedQuotaTotal" label="员工+亲友已用额度总额" min-width="150">
              <template #default="{ row }">{{ formatNumber(row.allUsedQuotaTotal) }}</template>
            </el-table-column>
            <el-table-column prop="orderCnt" label="下单数量" min-width="100">
              <template #default="{ row }">{{ formatNumber(row.orderCnt) }}</template>
            </el-table-column>
            <el-table-column prop="friendAccountCnt" label="亲友账号数" min-width="110">
              <template #default="{ row }">{{ formatNumber(row.friendAccountCnt) }}</template>
            </el-table-column>
            <el-table-column prop="friendAccountUsedCnt" label="亲友账号在用数" min-width="120">
              <template #default="{ row }">{{ formatNumber(row.friendAccountUsedCnt) }}</template>
            </el-table-column>
            <el-table-column prop="friendAccountStatusText" label="亲友账号状态" min-width="110" />
            <el-table-column prop="friendOrderCntTotal" label="亲友账号下单总数" min-width="130">
              <template #default="{ row }">{{ formatNumber(row.friendOrderCntTotal) }}</template>
            </el-table-column>
          </el-table>

          <div class="employee-pagination">
            <el-pagination background layout="total, prev, pager, next, sizes" :current-page="employeePage.page"
              :page-size="employeePage.size" :page-sizes="[20, 50, 100]" :total="employeePage.total"
              @current-change="onEmployeePageChange" @size-change="onEmployeeSizeChange" />
          </div>
        </el-card>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Download, QuestionFilled } from '@element-plus/icons-vue'
import * as XLSX from 'xlsx'
import * as echarts from 'echarts'
import {
  execBurtonOrderSql,
  fetchBurtonTracking,
  getBurtonStoreConfig,
  loginBurtonStore
} from '@/utils/burtonDashboardApi'

const STORAGE_KEY = 'burton_dashboard_launch_date'
const AUTO_REFRESH_SECONDS = 30 * 60

const METRIC_DESC = {
  visitUv: '访问人数（UV）：去重用户数。',
  visitPv: '访问次数（PV）：访问事件总次数。',
  addUv: '加购人数：发生加购行为的去重用户数。',
  orderUserCnt: '下单人数：有下单行为的去重买家数。',
  orderUnpaidUserCnt: '订单未支付人数：下单但未支付的去重买家数。',
  payUserCnt: '支付人数：有支付行为的去重买家数。',
  totalOrderCnt: '总订单数：订单创建总数。',
  paidOrderCnt: '支付订单数：pay_time 不为空的订单数。',
  orderAmount: '下单金额：actual_pay_amount + shipping_fee。',
  payAmount: '支付金额：payment 字段汇总（仅 pay_time 不为空）。',
  orderItemQty: '下单总件数：按主单汇总子单 quantity（order_item）。',
  paidItemQty: '支付总件数：支付订单对应子单 quantity 汇总。',
  avgOrderAmount: '下单平均金额 = 下单总金额 ÷ 下单人数。',
  avgPayAmount: '支付平均金额 = 支付总金额 ÷ 支付人数。',
  orderRate: '下单转化率 = 总订单数 ÷ 访问人数。',
  orderLossRate: '订单流失率 = 订单未支付人数 ÷ 总订单数。',
  waitPayUserCnt: '待支付人数：处于待付款状态的去重买家数。',
  payRate: '支付转化率 = 支付人数 ÷ 访问人数。',
  waitPayLossRate: '待支付流失率 = 待支付人数 ÷（支付人数 + 待支付人数）。'
}

const WAIT_PAY_STATUSES = ['TRADE_NO_CREATE_PAY', 'WAIT_BUYER_PAY']
const CANCEL_ORDER_STATUSES = ['TRADE_CLOSED', 'TRADE_CLOSED_BY_PLATFORM', 'TRADE_CLOSED_BY_BUYER']
const STATUS_LABEL_MAP = {
  TRADE_NO_CREATE_PAY: '未创建交易单',
  WAIT_BUYER_PAY: '待付款',
  WAIT_SELLER_SEND_GOODS: '待发货',
  WAIT_BUYER_CONFIRM_GOODS: '待确认收货',
  TRADE_BUYER_SIGNED: '已签收',
  TRADE_FINISHED: '交易成功',
  TRADE_CLOSED: '交易关闭(退款成功)',
  TRADE_CLOSED_BY_PLATFORM: '交易关闭(平台关闭)',
  TRADE_CLOSED_BY_BUYER: '交易关闭(买家关闭)'
}
const STATUS_ORDER = Object.keys(STATUS_LABEL_MAP)

const loading = ref(false)
const activeTab = ref('today')
const launchDate = ref('')
const queryDate = ref('')
const lastRefreshTime = ref('')
const historyRange = ref([])
const todayTrendMetric = ref('visitUv')
const historyTrendMetric = ref('visitUv')
const todayExportSelections = ref(
  [
    'today-report'
    , 'today-summary'
    , 'today-hour'
  ])
const hourTableRef = ref(null)
const todayTrendChartRef = ref(null)
const todayStatusChartRef = ref(null)
const historyTrendChartRef = ref(null)
const dashboardConfig = ref(null)
const suppressLaunchWatch = ref(true)
const autoRefreshSecondsLeft = ref(AUTO_REFRESH_SECONDS)
let countdownTimer = null
let resizeObserverErrorHandler = null
let latestRequestId = 0
let chartResizeTimer = null
let todayTrendChart = null
let todayStatusChart = null
let historyTrendChart = null

const todayOverview = ref({
  visitPv: 0,
  visitUv: 0,
  addPv: 0,
  addUv: 0,
  totalOrderCnt: 0,
  paidOrderCnt: 0,
  payUserCnt: 0,
  waitPayUserCnt: 0,
  orderUserCnt: 0,
  orderUnpaidUserCnt: 0,
  orderCnt: 0,
  orderAmount: 0,
  payAmount: 0,
  orderItemQty: 0,
  paidItemQty: 0,
  canceledOrderCnt: 0
})
const todayHourlyRows = ref([])
const todayOrderStatusRows = ref([])
const historyRows = ref([])
const todayOverviewMom = ref({})
const historyWindow = ref({
  start: '',
  end: ''
})
const employeeLoading = ref(false)
const employeeActivities = ref([])
const selectedEmployeeActivityId = ref(null)
const employeeSummary = ref({
  employeeAccountCnt: 0,
  employeeAccountUsedCnt: 0,
  employeeAccountUnusedCnt: 0,
  friendAccountCnt: 0,
  friendAccountUsedCnt: 0,
  friendAccountUnusedCnt: 0,
  employeeOrderCntTotal: 0,
  friendOrderCntTotal: 0,
  allOrderCntTotal: 0,
  employeePaidOrderCntTotal: 0,
  friendPaidOrderCntTotal: 0,
  allPaidOrderCntTotal: 0,
  employeeOrderAmountTotal: 0,
  friendOrderAmountTotal: 0,
  allOrderAmountTotal: 0,
  employeePayAmountTotal: 0,
  friendPayAmountTotal: 0,
  allPayAmountTotal: 0,
  employeeOrderItemQtyTotal: 0,
  friendOrderItemQtyTotal: 0,
  allOrderItemQtyTotal: 0,
  employeePaidItemQtyTotal: 0,
  friendPaidItemQtyTotal: 0,
  allPaidItemQtyTotal: 0,
  employeeUsedQuotaTotal: 0,
  friendUsedQuotaTotal: 0,
  allUsedQuotaTotal: 0
})
const employeeExportSelections = ref([
  // 'summary',
  // 'employee-list',
  // 'friend-list',
  'friend-report',
  // 'employee-rank',
  // 'friend-rank',
  // 'combined-rank'
])
const employeeSearchForm = ref({
  employeeName: '',
  groupName: '',
  employeeAccountStatus: '',
  friendAccountStatus: ''
})
const employeePage = ref({
  page: 1,
  size: 20,
  total: 0
})
const employeeAccountRows = ref([])
const employeeOrderRankRows = ref([])
const friendOrderRankRows = ref([])
const combinedOrderRankRows = ref([])
const employeeRankSortMetric = ref('orderCnt')
const friendRankSortMetric = ref('orderCnt')
const combinedRankSortMetric = ref('totalOrderCnt')

const employeeRankMetricOptions = [
  { label: '下单总订单数', value: 'orderCnt' },
  { label: '支付总订单数', value: 'paidOrderCnt' },
  { label: '下单总金额', value: 'orderAmount' },
  { label: '支付总金额', value: 'payAmount' },
  { label: '下单总件数', value: 'orderItemQty' },
  { label: '支付总件数', value: 'paidItemQty' }
]
const friendRankMetricOptions = [
  { label: '下单总订单数', value: 'orderCnt' },
  { label: '支付总订单数', value: 'paidOrderCnt' },
  { label: '下单总金额', value: 'orderAmount' },
  { label: '支付总金额', value: 'payAmount' },
  { label: '下单总件数', value: 'orderItemQty' },
  { label: '支付总件数', value: 'paidItemQty' }
]
const combinedRankMetricOptions = [
  { label: '下单总订单数', value: 'totalOrderCnt' },
  { label: '支付总订单数', value: 'paidOrderCnt' },
  { label: '下单总金额', value: 'orderAmount' },
  { label: '支付总金额', value: 'payAmount' },
  { label: '下单总件数', value: 'orderItemQty' },
  { label: '支付总件数', value: 'paidItemQty' }
]

const trendMetricOptions = [
  { label: '访问次数', value: 'visitPv' },
  { label: '访问人数', value: 'visitUv' },
  { label: '加购人数', value: 'addUv' },
  { label: '总订单数', value: 'totalOrderCnt' },
  { label: '下单人数', value: 'orderUserCnt' },
  { label: '订单未支付人数', value: 'orderUnpaidUserCnt' },
  { label: '支付订单数', value: 'paidOrderCnt' },
  { label: '支付人数', value: 'payUserCnt' },
  { label: '待支付人数', value: 'waitPayUserCnt' },
  { label: '下单金额', value: 'orderAmount' },
  { label: '支付金额', value: 'payAmount' },
  { label: '下单总件数', value: 'orderItemQty' },
  { label: '支付总件数', value: 'paidItemQty' },
  { label: '下单平均金额', value: 'avgOrderAmount' },
  { label: '支付平均金额', value: 'avgPayAmount' },
  { label: '下单转化率', value: 'orderRate' },
  { label: '订单流失率', value: 'orderLossRate' },
  { label: '支付转化率', value: 'payRate' },
  { label: '待支付流失率', value: 'waitPayLossRate' }
]

const todayKpiCards = computed(() => {
  const data = todayOverview.value
  return [
    {
      key: 'visitPv',
      label: '访问次数',
      value: formatNumber(data.visitPv),
      mom: todayOverviewMom.value.visitPvMom,
      desc: METRIC_DESC.visitPv
    },
    {
      key: 'visitUv',
      label: '访问人数',
      value: formatNumber(data.visitUv),
      mom: todayOverviewMom.value.visitUvMom,
      desc: METRIC_DESC.visitUv
    },
    {
      key: 'addUv',
      label: '加购人数',
      value: formatNumber(data.addUv),
      mom: todayOverviewMom.value.addUvMom,
      desc: METRIC_DESC.addUv
    },
    {
      key: 'totalOrderCnt',
      label: '总订单数',
      value: formatNumber(data.totalOrderCnt),
      mom: todayOverviewMom.value.totalOrderCntMom,
      desc: METRIC_DESC.totalOrderCnt
    },
    {
      key: 'orderUserCnt',
      label: '下单人数',
      value: formatNumber(data.orderUserCnt),
      mom: todayOverviewMom.value.orderUserCntMom,
      desc: METRIC_DESC.orderUserCnt
    },
    {
      key: 'orderUnpaidUserCnt',
      label: '订单未支付人数',
      value: formatNumber(data.orderUnpaidUserCnt),
      mom: todayOverviewMom.value.orderUnpaidUserCntMom,
      desc: METRIC_DESC.orderUnpaidUserCnt
    },
    {
      key: 'paidOrderCnt',
      label: '支付订单数',
      value: formatNumber(data.paidOrderCnt),
      mom: todayOverviewMom.value.paidOrderCntMom,
      desc: METRIC_DESC.paidOrderCnt
    },
    {
      key: 'payUserCnt',
      label: '支付人数',
      value: formatNumber(data.payUserCnt),
      mom: todayOverviewMom.value.payUserCntMom,
      desc: METRIC_DESC.payUserCnt
    },
    {
      key: 'waitPayUserCnt',
      label: '待支付人数',
      value: formatNumber(data.waitPayUserCnt),
      mom: todayOverviewMom.value.waitPayUserCntMom,
      desc: METRIC_DESC.waitPayUserCnt
    },
    {
      key: 'orderRate',
      label: '下单转化率',
      value: formatPercent(calcRate(data.totalOrderCnt, data.visitUv)),
      mom: todayOverviewMom.value.orderRateMom,
      desc: METRIC_DESC.orderRate
    },
    {
      key: 'orderLossRate',
      label: '订单流失率',
      value: formatPercent(calcRate(data.orderUnpaidUserCnt, data.totalOrderCnt)),
      mom: todayOverviewMom.value.orderLossRateMom,
      desc: METRIC_DESC.orderLossRate
    },
    {
      key: 'payRate',
      label: '支付转化率',
      value: formatPercent(calcRate(data.payUserCnt, data.visitUv)),
      mom: todayOverviewMom.value.payRateMom,
      desc: METRIC_DESC.payRate
    },
    {
      key: 'waitPayLossRate',
      label: '待支付流失率',
      value: formatPercent(calcRate(data.waitPayUserCnt, data.payUserCnt + data.waitPayUserCnt)),
      mom: todayOverviewMom.value.waitPayLossRateMom,
      desc: METRIC_DESC.waitPayLossRate
    },
    {
      key: 'orderAmount',
      label: '下单金额',
      value: formatAmount(data.orderAmount),
      mom: todayOverviewMom.value.orderAmountMom,
      desc: METRIC_DESC.orderAmount
    },
    {
      key: 'orderItemQty',
      label: '下单总件数',
      value: formatNumber(data.orderItemQty),
      mom: todayOverviewMom.value.orderItemQtyMom,
      desc: METRIC_DESC.orderItemQty
    },
    {
      key: 'avgOrderAmount',
      label: '下单平均金额',
      value: formatAmount(calcRate(data.orderAmount, data.orderUserCnt)),
      mom: todayOverviewMom.value.avgOrderAmountMom,
      desc: METRIC_DESC.avgOrderAmount
    },
    {
      key: 'payAmount',
      label: '支付金额',
      value: formatAmount(data.payAmount),
      mom: todayOverviewMom.value.payAmountMom,
      desc: METRIC_DESC.payAmount
    },
    {
      key: 'paidItemQty',
      label: '支付总件数',
      value: formatNumber(data.paidItemQty),
      mom: todayOverviewMom.value.paidItemQtyMom,
      desc: METRIC_DESC.paidItemQty
    },
    {
      key: 'avgPayAmount',
      label: '支付平均金额',
      value: formatAmount(calcRate(data.payAmount, data.payUserCnt)),
      mom: todayOverviewMom.value.avgPayAmountMom,
      desc: METRIC_DESC.avgPayAmount
    }
  ]
})

const historyOverview = computed(() => {
  return (historyRows.value || []).reduce(
    (acc, row) => {
      acc.visitPv += toNumber(row.visitPv)
      acc.visitUv += toNumber(row.visitUv)
      acc.addUv += toNumber(row.addUv)
      acc.totalOrderCnt += toNumber(row.totalOrderCnt)
      acc.orderUserCnt += toNumber(row.orderUserCnt)
      acc.orderUnpaidUserCnt += toNumber(row.orderUnpaidUserCnt)
      acc.paidOrderCnt += toNumber(row.paidOrderCnt)
      acc.payUserCnt += toNumber(row.payUserCnt)
      acc.waitPayUserCnt += toNumber(row.waitPayUserCnt)
      acc.orderAmount += toNumber(row.orderAmount)
      acc.payAmount += toNumber(row.payAmount)
      acc.orderItemQty += toNumber(row.orderItemQty)
      acc.paidItemQty += toNumber(row.paidItemQty)
      return acc
    },
    {
      visitPv: 0,
      visitUv: 0,
      addUv: 0,
      totalOrderCnt: 0,
      orderUserCnt: 0,
      orderUnpaidUserCnt: 0,
      paidOrderCnt: 0,
      payUserCnt: 0,
      waitPayUserCnt: 0,
      orderAmount: 0,
      payAmount: 0,
      orderItemQty: 0,
      paidItemQty: 0
    }
  )
})

const historyKpiCards = computed(() => {
  const data = historyOverview.value
  return [
    { key: 'visitPv', label: '访问次数', value: formatNumber(data.visitPv), desc: METRIC_DESC.visitPv },
    { key: 'visitUv', label: '访问人数', value: formatNumber(data.visitUv), desc: METRIC_DESC.visitUv },
    { key: 'addUv', label: '加购人数', value: formatNumber(data.addUv), desc: METRIC_DESC.addUv },
    { key: 'totalOrderCnt', label: '总订单数', value: formatNumber(data.totalOrderCnt), desc: METRIC_DESC.totalOrderCnt },
    { key: 'orderUserCnt', label: '下单人数', value: formatNumber(data.orderUserCnt), desc: METRIC_DESC.orderUserCnt },
    { key: 'orderUnpaidUserCnt', label: '订单未支付人数', value: formatNumber(data.orderUnpaidUserCnt), desc: METRIC_DESC.orderUnpaidUserCnt },
    { key: 'paidOrderCnt', label: '支付订单数', value: formatNumber(data.paidOrderCnt), desc: METRIC_DESC.paidOrderCnt },
    { key: 'payUserCnt', label: '支付人数', value: formatNumber(data.payUserCnt), desc: METRIC_DESC.payUserCnt },
    { key: 'waitPayUserCnt', label: '待支付人数', value: formatNumber(data.waitPayUserCnt), desc: METRIC_DESC.waitPayUserCnt },
    { key: 'orderRate', label: '下单转化率', value: formatPercent(calcRate(data.totalOrderCnt, data.visitUv)), desc: METRIC_DESC.orderRate },
    { key: 'orderLossRate', label: '订单流失率', value: formatPercent(calcRate(data.orderUnpaidUserCnt, data.totalOrderCnt)), desc: METRIC_DESC.orderLossRate },
    { key: 'payRate', label: '支付转化率', value: formatPercent(calcRate(data.payUserCnt, data.visitUv)), desc: METRIC_DESC.payRate },
    { key: 'waitPayLossRate', label: '待支付流失率', value: formatPercent(calcRate(data.waitPayUserCnt, data.payUserCnt + data.waitPayUserCnt)), desc: METRIC_DESC.waitPayLossRate },
    { key: 'orderAmount', label: '下单金额', value: formatAmount(data.orderAmount), desc: METRIC_DESC.orderAmount },
    { key: 'orderItemQty', label: '下单总件数', value: formatNumber(data.orderItemQty), desc: METRIC_DESC.orderItemQty },
    { key: 'avgOrderAmount', label: '下单平均金额', value: formatAmount(calcRate(data.orderAmount, data.orderUserCnt)), desc: METRIC_DESC.avgOrderAmount },
    { key: 'payAmount', label: '支付金额', value: formatAmount(data.payAmount), desc: METRIC_DESC.payAmount },
    { key: 'paidItemQty', label: '支付总件数', value: formatNumber(data.paidItemQty), desc: METRIC_DESC.paidItemQty },
    { key: 'avgPayAmount', label: '支付平均金额', value: formatAmount(calcRate(data.payAmount, data.payUserCnt)), desc: METRIC_DESC.avgPayAmount }
  ]
})

const historyWindowText = computed(() => {
  if (!historyWindow.value.start || !historyWindow.value.end) return '-'
  return `${historyWindow.value.start} ~ ${historyWindow.value.end}`
})

const selectedEmployeeActivity = computed(() => {
  return employeeActivities.value.find((item) => Number(item.id) === Number(selectedEmployeeActivityId.value)) || null
})

const sortedEmployeeOrderRankRows = computed(() => {
  const key = employeeRankSortMetric.value
  return [...employeeOrderRankRows.value]
    .filter((item) => toNumber(item[key]) > 0)
    .sort((a, b) => toNumber(b[key]) - toNumber(a[key]))
})

const sortedFriendOrderRankRows = computed(() => {
  const key = friendRankSortMetric.value
  return [...friendOrderRankRows.value]
    .filter((item) => toNumber(item[key]) > 0)
    .sort((a, b) => toNumber(b[key]) - toNumber(a[key]))
})

const sortedCombinedOrderRankRows = computed(() => {
  const key = combinedRankSortMetric.value
  return [...combinedOrderRankRows.value]
    .filter((item) => toNumber(item[key]) > 0)
    .sort((a, b) => toNumber(b[key]) - toNumber(a[key]))
})

function trendMetricLabel(metric) {
  const target = trendMetricOptions.find((item) => item.value === metric)
  return target ? target.label : metric
}

function metricValueByKey(row, key) {
  if (!row) return 0
  if (key === 'orderRate') return calcRate(row.totalOrderCnt, row.visitUv)
  if (key === 'orderLossRate') return calcRate(row.orderUnpaidUserCnt, row.totalOrderCnt)
  if (key === 'payRate') return calcRate(row.payUserCnt, row.visitUv)
  if (key === 'waitPayLossRate') return calcRate(row.waitPayUserCnt, row.payUserCnt + row.waitPayUserCnt)
  return toNumber(row[key])
}

const disableFutureDate = (date) => {
  return date.getTime() > Date.now()
}

const refreshCountdownText = computed(() => {
  const total = toNumber(autoRefreshSecondsLeft.value)
  const mm = pad2(Math.floor(total / 60))
  const ss = pad2(total % 60)
  return `${mm}:${ss}`
})

const disableHistoryDate = (date) => {
  const tomorrowStart = addDays(startOfDay(new Date()), 1)
  const launchDay = parseDateStart(launchDate.value)
  if (date.getTime() >= tomorrowStart.getTime()) return true
  if (launchDay && date.getTime() < launchDay.getTime()) return true
  return false
}

const getNowDate = () => {
  const now = new Date()
  return formatDate(now)
}

function toNumber(value) {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

function pad2(num) {
  return String(num).padStart(2, '0')
}

function formatDate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

function formatDateTime(date) {
  return `${formatDate(date)} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
}

function parseDateTime(dateTimeText) {
  if (!dateTimeText) return null
  const normalized = String(dateTimeText).trim().replace(' ', 'T').slice(0, 19)
  const value = new Date(normalized)
  if (Number.isNaN(value.getTime())) return null
  return value
}

function formatDisplayDateTime(dateTimeText) {
  const parsed = parseDateTime(dateTimeText)
  return parsed ? formatDateTime(parsed) : '-'
}

function escapeSqlText(value) {
  return String(value || '').replace(/'/g, "''")
}

function getEmployeeActivityStatus(activity) {
  const now = new Date()
  const start = parseDateTime(activity?.start_time)
  const end = parseDateTime(activity?.end_time)
  if (!start) return '未知'
  if (now < start) return '未开始'
  if (end && now > end) return '已结束'
  return '进行中'
}

function parseDateStart(dateString) {
  if (!dateString) return null
  const text = String(dateString).trim()
  const dateOnly = text.slice(0, 10)
  const date = new Date(`${dateOnly}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function parseProjectStartDate(config) {
  const field = config?.project_start_time
  if (!field) return ''
  const parsed = parseDateStart(field)
  return parsed ? formatDate(parsed) : ''
}

function getDefaultHistoryRange(launchDateText) {
  const todayStart = startOfDay(new Date())
  const launchDay = parseDateStart(launchDateText)
  if (!launchDay || todayStart < launchDay) return []
  const near7Start = addDays(todayStart, -6)
  const start = launchDay > near7Start ? launchDay : near7Start
  return [formatDate(start), formatDate(todayStart)]
}

function normalizeHistoryRange(launchDay, todayStart) {
  const tomorrowStart = addDays(todayStart, 1)
  const near7Start = addDays(todayStart, -6)
  const defaultStart = launchDay > near7Start ? launchDay : near7Start
  let historyStart = defaultStart
  let historyEndExclusive = tomorrowStart

  const rangeStart = historyRange.value?.[0] ? parseDateStart(historyRange.value[0]) : null
  const rangeEnd = historyRange.value?.[1] ? parseDateStart(historyRange.value[1]) : null

  if (rangeStart) historyStart = rangeStart
  if (rangeEnd) historyEndExclusive = addDays(rangeEnd, 1)

  if (historyStart < launchDay) historyStart = launchDay
  if (historyEndExclusive > tomorrowStart) historyEndExclusive = tomorrowStart
  if (historyEndExclusive < launchDay) historyEndExclusive = launchDay

  return {
    historyStart,
    historyEndExclusive,
    hasHistory: historyStart < historyEndExclusive
  }
}

function startOfDay(date) {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}

function addDays(date, days) {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

function shouldUseTrackingTodayTable(targetDayStart, now = new Date()) {
  const todayStart = startOfDay(now)
  const yesterdayStart = addDays(todayStart, -1)
  const archiveSwitchTime = new Date(todayStart)
  archiveSwitchTime.setHours(3, 0, 0, 0)
  return (
    formatDate(targetDayStart) === formatDate(todayStart) ||
    (formatDate(targetDayStart) === formatDate(yesterdayStart) && now < archiveSwitchTime)
  )
}

function getDateRangeKeys(startDate, endDate, mode) {
  const keys = []
  if (mode === 'hour') {
    for (let i = 0; i < 24; i += 1) {
      keys.push(`${formatDate(startDate)} ${pad2(i)}`)
    }
    return keys
  }

  const cursor = new Date(startDate)
  while (cursor < endDate) {
    keys.push(formatDate(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return keys
}

function normalizeBucket(rawBucket, mode) {
  if (!rawBucket) return ''
  const text = String(rawBucket).trim()
  if (mode === 'hour') {
    return text.slice(0, 13)
  }
  return text.slice(0, 10)
}

function groupTrackingRows(rows, mode) {
  const result = {}
  rows.forEach((item) => {
    const bucket = normalizeBucket(item.day_date, mode)
    if (!bucket) return
    if (!result[bucket]) {
      result[bucket] = {
        visitPv: 0,
        visitUv: 0,
        addPv: 0,
        addUv: 0
      }
    }

    const indexName = String(item.indexname || '').toLowerCase()
    const pv = toNumber(item.pv)
    const uv = toNumber(item.uv)

    if (indexName.includes('visit')) {
      result[bucket].visitPv += pv
      result[bucket].visitUv += uv
      return
    }

    if (indexName.includes('addshop') || indexName.includes('add')) {
      result[bucket].addPv += pv
      result[bucket].addUv += uv
    }
  })
  return result
}

function rowArrayToMap(rows, keyField) {
  const map = {}
    ; (rows || []).forEach((row) => {
      if (!row[keyField]) return
      map[String(row[keyField])] = row
    })
  return map
}

function calcRate(numerator, denominator) {
  const d = toNumber(denominator)
  if (!d) return 0
  return toNumber(numerator) / d
}

function formatNumber(value) {
  return toNumber(value).toLocaleString('zh-CN')
}

function formatAmount(value) {
  return `￥${toNumber(value).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

function formatPercent(rate) {
  return `${(toNumber(rate) * 100).toFixed(2)}%`
}

function calcMomRate(current, previous) {
  const curr = toNumber(current)
  const prev = toNumber(previous)
  if (!prev) {
    if (!curr) return null
    return Infinity
  }
  return (curr - prev) / prev
}

function formatMomRate(rate) {
  if (rate === null || rate === undefined || Number.isNaN(rate)) return '--'
  if (!Number.isFinite(rate)) return '+∞'
  const sign = rate > 0 ? '+' : ''
  return `${sign}${(rate * 100).toFixed(2)}%`
}

function momArrow(rate) {
  if (rate === null || rate === undefined || Number.isNaN(rate) || rate === 0) return '—'
  if (!Number.isFinite(rate)) return '▲'
  return rate > 0 ? '▲' : '▼'
}

function momClass(rate) {
  if (rate === null || rate === undefined || Number.isNaN(rate) || rate === 0) return 'is-flat'
  return rate > 0 ? 'is-up' : 'is-down'
}

function isRateMetric(metricKey) {
  return metricKey === 'orderRate' || metricKey === 'orderLossRate' || metricKey === 'payRate' || metricKey === 'waitPayLossRate'
}

function disposeChart(instance) {
  if (instance) {
    instance.dispose()
  }
}

function initChartInstances() {
  if (todayTrendChartRef.value && !todayTrendChart) {
    todayTrendChart = echarts.init(todayTrendChartRef.value)
  }
  if (todayStatusChartRef.value && !todayStatusChart) {
    todayStatusChart = echarts.init(todayStatusChartRef.value)
  }
  if (historyTrendChartRef.value && !historyTrendChart) {
    historyTrendChart = echarts.init(historyTrendChartRef.value)
  }
}

function buildTrendChartOption({ rows, metricKey, chartTitle }) {
  const xData = rows.map((item) => item.bucket)
  const yData = rows.map((item) => metricValueByKey(item, metricKey))
  const rateMetric = isRateMetric(metricKey)
  const label = trendMetricLabel(metricKey)
  return {
    animationDuration: 350,
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value) => (rateMetric ? formatPercent(value) : toNumber(value).toLocaleString('zh-CN'))
    },
    grid: {
      top: 50,
      left: 60,
      right: 20,
      bottom: 60
    },
    title: {
      text: `${chartTitle}（${label}）`,
      left: 14,
      top: 8,
      textStyle: {
        color: '#24405c',
        fontSize: 13,
        fontWeight: 600
      }
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xData,
      axisLabel: {
        color: '#627894'
      },
      axisLine: {
        lineStyle: {
          color: '#8fa3bb'
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#627894',
        formatter: (value) => (rateMetric ? formatPercent(value) : toNumber(value).toLocaleString('zh-CN'))
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(143, 163, 187, 0.28)',
          type: 'dashed'
        }
      }
    },
    series: [
      {
        name: label,
        type: 'line',
        smooth: true,
        showSymbol: true,
        symbolSize: 6,
        data: yData,
        itemStyle: {
          color: '#1877f2'
        },
        lineStyle: {
          width: 3,
          color: '#1877f2'
        },
        areaStyle: {
          color: 'rgba(24, 119, 242, 0.16)'
        }
      }
    ],
    graphic: !rows.length
      ? {
        type: 'text',
        left: 'center',
        top: 'middle',
        style: {
          text: '暂无数据',
          fill: '#6e839d',
          fontSize: 14
        }
      }
      : []
  }
}

function buildStatusChartOption() {
  const sourceRows = todayOrderStatusRows.value || []
  const data = sourceRows.map((row) => ({
    name: row.statusLabel,
    value: toNumber(row.count),
    statusCode: row.status
  })).filter((item) => item.value > 0)
  return {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const count = toNumber(params.value)
        const ratio = calcRate(count, todayOverview.value.orderCnt)
        return `${params.name}<br/>订单数：${formatNumber(count)}<br/>占比：${formatPercent(ratio)}`
      }
    },
    legend: {
      bottom: 6,
      type: 'scroll',
      textStyle: {
        color: '#4a607a'
      }
    },
    series: [
      {
        type: 'pie',
        radius: ['35%', '68%'],
        center: ['50%', '46%'],
        label: {
          color: '#344e6b',
          formatter: ({ name, value }) => `${name}\n${formatNumber(value)}`
        },
        data
      }
    ],
    graphic: !data.length
      ? {
        type: 'text',
        left: 'center',
        top: 'middle',
        style: {
          text: '暂无数据',
          fill: '#6e839d',
          fontSize: 14
        }
      }
      : []
  }
}

function renderTodayCharts() {
  initChartInstances()
  if (todayTrendChart) {
    todayTrendChart.setOption(
      buildTrendChartOption({
        rows: todayHourlyRows.value,
        metricKey: todayTrendMetric.value,
        chartTitle: '今日小时趋势'
      }),
      true
    )
  }
  if (todayStatusChart) {
    todayStatusChart.setOption(buildStatusChartOption(), true)
  }
}

function renderHistoryChart() {
  initChartInstances()
  if (!historyTrendChart) return
  const ascRows = [...historyRows.value].reverse()
  historyTrendChart.setOption(
    buildTrendChartOption({
      rows: ascRows,
      metricKey: historyTrendMetric.value,
      chartTitle: '历史日趋势'
    }),
    true
  )
}

function resizeAllCharts() {
  if (todayTrendChart) todayTrendChart.resize()
  if (todayStatusChart) todayStatusChart.resize()
  if (historyTrendChart) historyTrendChart.resize()
}

function handleWindowResize() {
  if (chartResizeTimer) clearTimeout(chartResizeTimer)
  chartResizeTimer = setTimeout(() => {
    resizeAllCharts()
  }, 120)
}

function enrichMomRows(rows) {
  return rows.map((row, index) => {
    const currentOrderRate = calcRate(row.totalOrderCnt, row.visitUv)
    const currentOrderLossRate = calcRate(row.orderUnpaidUserCnt, row.totalOrderCnt)
    const currentPayRate = calcRate(row.payUserCnt, row.visitUv)
    const currentWaitPayLossRate = calcRate(row.waitPayUserCnt, row.payUserCnt + row.waitPayUserCnt)
    const currentAvgOrderAmount = calcRate(row.orderAmount, row.orderUserCnt)
    const currentAvgPayAmount = calcRate(row.payAmount, row.payUserCnt)

    if (index === 0) {
      return {
        ...row,
        visitPvMom: null,
        visitUvMom: null,
        addUvMom: null,
        totalOrderCntMom: null,
        orderUserCntMom: null,
        orderUnpaidUserCntMom: null,
        paidOrderCntMom: null,
        payUserMom: null,
        waitPayUserCntMom: null,
        orderRateMom: null,
        orderLossRateMom: null,
        payRateMom: null,
        orderAmountMom: null,
        orderItemQtyMom: null,
        avgOrderAmountMom: null,
        payAmountMom: null,
        paidItemQtyMom: null,
        avgPayAmountMom: null,
        waitPayLossRateMom: null
      }
    }
    const prev = rows[index - 1]
    const prevOrderRate = calcRate(prev.totalOrderCnt, prev.visitUv)
    const prevOrderLossRate = calcRate(prev.orderUnpaidUserCnt, prev.totalOrderCnt)
    const prevPayRate = calcRate(prev.payUserCnt, prev.visitUv)
    const prevWaitPayLossRate = calcRate(prev.waitPayUserCnt, prev.payUserCnt + prev.waitPayUserCnt)
    const prevAvgOrderAmount = calcRate(prev.orderAmount, prev.orderUserCnt)
    const prevAvgPayAmount = calcRate(prev.payAmount, prev.payUserCnt)
    return {
      ...row,
      visitPvMom: calcMomRate(row.visitPv, prev.visitPv),
      visitUvMom: calcMomRate(row.visitUv, prev.visitUv),
      addUvMom: calcMomRate(row.addUv, prev.addUv),
      totalOrderCntMom: calcMomRate(row.totalOrderCnt, prev.totalOrderCnt),
      orderUserCntMom: calcMomRate(row.orderUserCnt, prev.orderUserCnt),
      orderUnpaidUserCntMom: calcMomRate(row.orderUnpaidUserCnt, prev.orderUnpaidUserCnt),
      paidOrderCntMom: calcMomRate(row.paidOrderCnt, prev.paidOrderCnt),
      payUserMom: calcMomRate(row.payUserCnt, prev.payUserCnt),
      waitPayUserCntMom: calcMomRate(row.waitPayUserCnt, prev.waitPayUserCnt),
      orderRateMom: calcMomRate(currentOrderRate, prevOrderRate),
      orderLossRateMom: calcMomRate(currentOrderLossRate, prevOrderLossRate),
      payRateMom: calcMomRate(currentPayRate, prevPayRate),
      orderAmountMom: calcMomRate(row.orderAmount, prev.orderAmount),
      orderItemQtyMom: calcMomRate(row.orderItemQty, prev.orderItemQty),
      avgOrderAmountMom: calcMomRate(currentAvgOrderAmount, prevAvgOrderAmount),
      payAmountMom: calcMomRate(row.payAmount, prev.payAmount),
      paidItemQtyMom: calcMomRate(row.paidItemQty, prev.paidItemQty),
      avgPayAmountMom: calcMomRate(currentAvgPayAmount, prevAvgPayAmount),
      waitPayLossRateMom: calcMomRate(currentWaitPayLossRate, prevWaitPayLossRate)
    }
  })
}

function buildTrackingParams({ start, end, formatTime, table }) {
  return {
    formatTime,
    startTime: formatDateTime(start),
    endTime: formatDateTime(end),
    table
  }
}

function joinSqlIn(values) {
  return values.map((item) => `'${item}'`).join(',')
}

function buildOrderSummarySql(start, end) {
  const waitPayIn = joinSqlIn(WAIT_PAY_STATUSES)
  const cancelIn = joinSqlIn(CANCEL_ORDER_STATUSES)

  return `
SELECT
  COUNT(*) AS total_order_cnt,
  SUM(CASE WHEN o.pay_time IS NOT NULL THEN 1 ELSE 0 END) AS paid_order_cnt,
  SUM(CASE WHEN o.order_status IN (${cancelIn}) THEN 1 ELSE 0 END) AS canceled_order_cnt,
  SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0)) AS order_amount,
  SUM(COALESCE(oi.total_quantity, 0)) AS order_item_qty,
  SUM(
    CASE
      WHEN o.pay_time IS NOT NULL
      THEN COALESCE(o.payment, 0)
      ELSE 0
    END
  ) AS pay_amount,
  SUM(
    CASE
      WHEN o.pay_time IS NOT NULL
      THEN COALESCE(oi.total_quantity, 0)
      ELSE 0
    END
  ) AS paid_item_qty,
  COUNT(DISTINCT o.buyer_id) AS order_user_cnt,
  COUNT(
    DISTINCT CASE
      WHEN o.pay_time IS NULL
      THEN o.buyer_id
    END
  ) AS order_unpaid_user_cnt,
  COUNT(
    DISTINCT CASE
      WHEN o.pay_time IS NOT NULL
      THEN o.buyer_id
    END
  ) AS pay_user_cnt,
  COUNT(
    DISTINCT CASE
      WHEN o.pay_time IS NULL AND o.order_status IN (${waitPayIn})
      THEN o.buyer_id
    END
  ) AS wait_pay_user_cnt
FROM store_order o
LEFT JOIN (
  SELECT
    order_id,
    SUM(COALESCE(quantity, 0)) AS total_quantity
  FROM order_item
  WHERE isdeleted = 0
  GROUP BY order_id
) oi ON oi.order_id = o.id
WHERE o.isdeleted = 0
  AND o.create_time >= '${formatDateTime(start)}'
  AND o.create_time < '${formatDateTime(end)}'
;`.trim()
}

function buildOrderGroupSql(start, end, mode) {
  const waitPayIn = joinSqlIn(WAIT_PAY_STATUSES)
  const cancelIn = joinSqlIn(CANCEL_ORDER_STATUSES)
  const timeFormat = mode === 'hour' ? '%Y-%m-%d %H' : '%Y-%m-%d'
  const bucketField = mode === 'hour' ? 'hour_bucket' : 'day_bucket'

  return `
SELECT
  DATE_FORMAT(o.create_time, '${timeFormat}') AS ${bucketField},
  COUNT(*) AS total_order_cnt,
  SUM(CASE WHEN o.order_status IN (${cancelIn}) THEN 1 ELSE 0 END) AS canceled_order_cnt,
  COUNT(DISTINCT o.buyer_id) AS order_user_cnt,
  COUNT(
    DISTINCT CASE
      WHEN o.pay_time IS NULL
      THEN o.buyer_id
    END
  ) AS order_unpaid_user_cnt,
  SUM(CASE WHEN o.pay_time IS NOT NULL THEN 1 ELSE 0 END) AS paid_order_cnt,
  SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0)) AS order_amount,
  SUM(COALESCE(oi.total_quantity, 0)) AS order_item_qty,
  SUM(
    CASE
      WHEN o.pay_time IS NOT NULL
      THEN COALESCE(o.payment, 0)
      ELSE 0
    END
  ) AS pay_amount,
  SUM(
    CASE
      WHEN o.pay_time IS NOT NULL
      THEN COALESCE(oi.total_quantity, 0)
      ELSE 0
    END
  ) AS paid_item_qty,
  COUNT(
    DISTINCT CASE
      WHEN o.pay_time IS NOT NULL
      THEN o.buyer_id
    END
  ) AS pay_user_cnt,
  COUNT(
    DISTINCT CASE
      WHEN o.pay_time IS NULL AND o.order_status IN (${waitPayIn})
      THEN o.buyer_id
    END
  ) AS wait_pay_user_cnt
FROM store_order o
LEFT JOIN (
  SELECT
    order_id,
    SUM(COALESCE(quantity, 0)) AS total_quantity
  FROM order_item
  WHERE isdeleted = 0
  GROUP BY order_id
) oi ON oi.order_id = o.id
WHERE o.isdeleted = 0
  AND o.create_time >= '${formatDateTime(start)}'
  AND o.create_time < '${formatDateTime(end)}'
GROUP BY DATE_FORMAT(o.create_time, '${timeFormat}')
ORDER BY DATE_FORMAT(o.create_time, '${timeFormat}') ASC
;`.trim()
}

function buildOrderStatusSql(start, end) {
  return `
SELECT
  o.order_status,
  COUNT(*) AS status_cnt
FROM store_order o
WHERE o.isdeleted = 0
  AND o.create_time >= '${formatDateTime(start)}'
  AND o.create_time < '${formatDateTime(end)}'
GROUP BY o.order_status
ORDER BY status_cnt DESC
;`.trim()
}

function buildEmployeeActivitySql() {
  return `
SELECT
  id,
  name,
  start_time,
  end_time
FROM employee_activity
WHERE isdeleted = 0
ORDER BY id DESC
;`.trim()
}

function buildEmployeeSummarySql(activityId, startTime, endTime) {
  return `
SELECT
  (
    SELECT COUNT(*)
    FROM employee_account ea0
    WHERE ea0.employee_activity_id = ${Number(activityId)}
  ) AS employee_account_cnt,
  (
    SELECT SUM(CASE WHEN COALESCE(ea0.used_quota, 0) > 0 THEN 1 ELSE 0 END)
    FROM employee_account ea0
    WHERE ea0.employee_activity_id = ${Number(activityId)}
  ) AS employee_account_used_cnt,
  (
    SELECT SUM(CASE WHEN COALESCE(ea0.used_quota, 0) = 0 THEN 1 ELSE 0 END)
    FROM employee_account ea0
    WHERE ea0.employee_activity_id = ${Number(activityId)}
  ) AS employee_account_unused_cnt,
  (
    SELECT SUM(COALESCE(ea0.used_quota, 0))
    FROM employee_account ea0
    WHERE ea0.employee_activity_id = ${Number(activityId)}
  ) AS employee_used_quota_total,
  (
    SELECT COUNT(*)
    FROM friend_account fa0
    WHERE fa0.employee_activity_id = ${Number(activityId)}
  ) AS friend_account_cnt,
  (
    SELECT SUM(CASE WHEN COALESCE(fa0.used_quota, 0) > 0 THEN 1 ELSE 0 END)
    FROM friend_account fa0
    WHERE fa0.employee_activity_id = ${Number(activityId)}
  ) AS friend_account_used_cnt,
  (
    SELECT SUM(CASE WHEN COALESCE(fa0.used_quota, 0) = 0 THEN 1 ELSE 0 END)
    FROM friend_account fa0
    WHERE fa0.employee_activity_id = ${Number(activityId)}
  ) AS friend_account_unused_cnt,
  (
    SELECT SUM(COALESCE(fa0.used_quota, 0))
    FROM friend_account fa0
    WHERE fa0.employee_activity_id = ${Number(activityId)}
  ) AS friend_used_quota_total,
  (
    SELECT COUNT(DISTINCT o.id)
    FROM store_order o
    INNER JOIN (
      SELECT ea2.user_id
      FROM employee_account ea2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM employee_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = ea2.id
      WHERE ea2.employee_activity_id = ${Number(activityId)}
    ) em ON em.user_id = o.creator_id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS employee_order_cnt_total,
  (
    SELECT COUNT(DISTINCT o.id)
    FROM store_order o
    INNER JOIN (
      SELECT fa2.user_id
      FROM friend_account fa2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM friend_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = fa2.id
      WHERE fa2.employee_activity_id = ${Number(activityId)}
    ) fm ON fm.user_id = o.creator_id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS friend_order_cnt_total,
  (
    SELECT SUM(CASE WHEN o.pay_time IS NOT NULL THEN 1 ELSE 0 END)
    FROM store_order o
    INNER JOIN (
      SELECT ea2.user_id
      FROM employee_account ea2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM employee_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = ea2.id
      WHERE ea2.employee_activity_id = ${Number(activityId)}
    ) em ON em.user_id = o.creator_id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS employee_paid_order_cnt_total,
  (
    SELECT SUM(CASE WHEN o.pay_time IS NOT NULL THEN 1 ELSE 0 END)
    FROM store_order o
    INNER JOIN (
      SELECT fa2.user_id
      FROM friend_account fa2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM friend_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = fa2.id
      WHERE fa2.employee_activity_id = ${Number(activityId)}
    ) fm ON fm.user_id = o.creator_id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS friend_paid_order_cnt_total,
  (
    SELECT SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0))
    FROM store_order o
    INNER JOIN (
      SELECT ea2.user_id
      FROM employee_account ea2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM employee_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = ea2.id
      WHERE ea2.employee_activity_id = ${Number(activityId)}
    ) em ON em.user_id = o.creator_id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS employee_order_amount_total,
  (
    SELECT SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0))
    FROM store_order o
    INNER JOIN (
      SELECT fa2.user_id
      FROM friend_account fa2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM friend_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = fa2.id
      WHERE fa2.employee_activity_id = ${Number(activityId)}
    ) fm ON fm.user_id = o.creator_id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS friend_order_amount_total,
  (
    SELECT SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(o.payment, 0) ELSE 0 END)
    FROM store_order o
    INNER JOIN (
      SELECT ea2.user_id
      FROM employee_account ea2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM employee_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = ea2.id
      WHERE ea2.employee_activity_id = ${Number(activityId)}
    ) em ON em.user_id = o.creator_id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS employee_pay_amount_total,
  (
    SELECT SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(o.payment, 0) ELSE 0 END)
    FROM store_order o
    INNER JOIN (
      SELECT fa2.user_id
      FROM friend_account fa2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM friend_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = fa2.id
      WHERE fa2.employee_activity_id = ${Number(activityId)}
    ) fm ON fm.user_id = o.creator_id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS friend_pay_amount_total,
  (
    SELECT SUM(COALESCE(oi.total_quantity, 0))
    FROM store_order o
    INNER JOIN (
      SELECT ea2.user_id
      FROM employee_account ea2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM employee_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = ea2.id
      WHERE ea2.employee_activity_id = ${Number(activityId)}
    ) em ON em.user_id = o.creator_id
    LEFT JOIN (
      SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
      FROM order_item
      WHERE isdeleted = 0
      GROUP BY order_id
    ) oi ON oi.order_id = o.id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS employee_order_item_qty_total,
  (
    SELECT SUM(COALESCE(oi.total_quantity, 0))
    FROM store_order o
    INNER JOIN (
      SELECT fa2.user_id
      FROM friend_account fa2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM friend_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = fa2.id
      WHERE fa2.employee_activity_id = ${Number(activityId)}
    ) fm ON fm.user_id = o.creator_id
    LEFT JOIN (
      SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
      FROM order_item
      WHERE isdeleted = 0
      GROUP BY order_id
    ) oi ON oi.order_id = o.id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS friend_order_item_qty_total,
  (
    SELECT SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(oi.total_quantity, 0) ELSE 0 END)
    FROM store_order o
    INNER JOIN (
      SELECT ea2.user_id
      FROM employee_account ea2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM employee_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = ea2.id
      WHERE ea2.employee_activity_id = ${Number(activityId)}
    ) em ON em.user_id = o.creator_id
    LEFT JOIN (
      SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
      FROM order_item
      WHERE isdeleted = 0
      GROUP BY order_id
    ) oi ON oi.order_id = o.id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS employee_paid_item_qty_total,
  (
    SELECT SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(oi.total_quantity, 0) ELSE 0 END)
    FROM store_order o
    INNER JOIN (
      SELECT fa2.user_id
      FROM friend_account fa2
      INNER JOIN (
        SELECT user_id, MAX(id) AS max_id
        FROM friend_account
        WHERE employee_activity_id = ${Number(activityId)}
        GROUP BY user_id
      ) pick ON pick.max_id = fa2.id
      WHERE fa2.employee_activity_id = ${Number(activityId)}
    ) fm ON fm.user_id = o.creator_id
    LEFT JOIN (
      SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
      FROM order_item
      WHERE isdeleted = 0
      GROUP BY order_id
    ) oi ON oi.order_id = o.id
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
  ) AS friend_paid_item_qty_total
FROM (SELECT 1 AS x) t
;`.trim()
}

function buildEmployeeOrderRankSql(activityId, startTime, endTime) {
  return `
SELECT
  e.name AS employee_name,
  e.employee_number,
  eg.group_name,
  COALESCE(eq.employee_used_quota_total, 0) AS employee_used_quota_total,
  COALESCE(fq.friend_used_quota_total, 0) AS friend_used_quota_total,
  COALESCE(eq.employee_used_quota_total, 0) + COALESCE(fq.friend_used_quota_total, 0) AS all_used_quota_total,
  COUNT(DISTINCT o.id) AS order_cnt,
  SUM(CASE WHEN o.pay_time IS NOT NULL THEN 1 ELSE 0 END) AS paid_order_cnt,
  SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0)) AS order_amount,
  SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(o.payment, 0) ELSE 0 END) AS pay_amount,
  SUM(COALESCE(oi.total_quantity, 0)) AS order_item_qty,
  SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(oi.total_quantity, 0) ELSE 0 END) AS paid_item_qty
FROM store_order o
INNER JOIN (
  SELECT ea2.user_id, ea2.employee_id
  FROM employee_account ea2
  INNER JOIN (
    SELECT user_id, MAX(id) AS max_id
    FROM employee_account
    WHERE employee_activity_id = ${Number(activityId)}
    GROUP BY user_id
  ) pick ON pick.max_id = ea2.id
  WHERE ea2.employee_activity_id = ${Number(activityId)}
) em ON em.user_id = o.creator_id
LEFT JOIN employee e ON e.id = em.employee_id
LEFT JOIN employee_group eg ON eg.id = e.employee_group_id
LEFT JOIN (
  SELECT
    employee_id,
    SUM(COALESCE(ea3.used_quota, 0)) AS employee_used_quota_total
  FROM employee_account ea3
  WHERE ea3.employee_activity_id = ${Number(activityId)}
  GROUP BY employee_id
) eq ON eq.employee_id = em.employee_id
LEFT JOIN (
  SELECT
    ea3.employee_id,
    SUM(COALESCE(fa3.used_quota, 0)) AS friend_used_quota_total
  FROM employee_account ea3
  LEFT JOIN friend_account fa3
    ON fa3.employee_account_id = ea3.id
    AND fa3.employee_activity_id = ${Number(activityId)}
  WHERE ea3.employee_activity_id = ${Number(activityId)}
  GROUP BY ea3.employee_id
) fq ON fq.employee_id = em.employee_id
LEFT JOIN (
  SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
  FROM order_item
  WHERE isdeleted = 0
  GROUP BY order_id
) oi ON oi.order_id = o.id
WHERE o.isdeleted = 0
  AND o.create_time >= '${startTime}'
  AND o.create_time < '${endTime}'
GROUP BY e.name, e.employee_number, eg.group_name, eq.employee_used_quota_total, fq.friend_used_quota_total
HAVING COUNT(DISTINCT o.id) > 0
ORDER BY order_cnt DESC
;`.trim()
}

function buildFriendOrderRankSql(activityId, startTime, endTime) {
  return `
SELECT
  fe.name AS friend_name,
  fe.employee_number AS friend_employee_number,
  oe.name AS owner_employee_name,
  COALESCE(eq.employee_used_quota_total, 0) AS employee_used_quota_total,
  COALESCE(fq.friend_used_quota_total, 0) AS friend_used_quota_total,
  COALESCE(eq.employee_used_quota_total, 0) + COALESCE(fq.friend_used_quota_total, 0) AS all_used_quota_total,
  COUNT(DISTINCT o.id) AS order_cnt,
  SUM(CASE WHEN o.pay_time IS NOT NULL THEN 1 ELSE 0 END) AS paid_order_cnt,
  SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0)) AS order_amount,
  SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(o.payment, 0) ELSE 0 END) AS pay_amount,
  SUM(COALESCE(oi.total_quantity, 0)) AS order_item_qty,
  SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(oi.total_quantity, 0) ELSE 0 END) AS paid_item_qty
FROM store_order o
INNER JOIN (
  SELECT
    fa2.user_id,
    fa2.employee_id AS friend_employee_id,
    ea2.employee_id AS owner_employee_id
  FROM friend_account fa2
  INNER JOIN (
    SELECT user_id, MAX(id) AS max_id
    FROM friend_account
    WHERE employee_activity_id = ${Number(activityId)}
    GROUP BY user_id
  ) pick ON pick.max_id = fa2.id
  LEFT JOIN employee_account ea2 ON ea2.id = fa2.employee_account_id
  WHERE fa2.employee_activity_id = ${Number(activityId)}
) fm ON fm.user_id = o.creator_id
LEFT JOIN employee fe ON fe.id = fm.friend_employee_id
LEFT JOIN employee oe ON oe.id = fm.owner_employee_id
LEFT JOIN (
  SELECT
    employee_id,
    SUM(COALESCE(ea3.used_quota, 0)) AS employee_used_quota_total
  FROM employee_account ea3
  WHERE ea3.employee_activity_id = ${Number(activityId)}
  GROUP BY employee_id
) eq ON eq.employee_id = fm.owner_employee_id
LEFT JOIN (
  SELECT
    ea3.employee_id,
    SUM(COALESCE(fa3.used_quota, 0)) AS friend_used_quota_total
  FROM employee_account ea3
  LEFT JOIN friend_account fa3
    ON fa3.employee_account_id = ea3.id
    AND fa3.employee_activity_id = ${Number(activityId)}
  WHERE ea3.employee_activity_id = ${Number(activityId)}
  GROUP BY ea3.employee_id
) fq ON fq.employee_id = fm.owner_employee_id
LEFT JOIN (
  SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
  FROM order_item
  WHERE isdeleted = 0
  GROUP BY order_id
) oi ON oi.order_id = o.id
WHERE o.isdeleted = 0
  AND o.create_time >= '${startTime}'
  AND o.create_time < '${endTime}'
GROUP BY fe.name, fe.employee_number, oe.name, eq.employee_used_quota_total, fq.friend_used_quota_total
HAVING COUNT(DISTINCT o.id) > 0
ORDER BY order_cnt DESC
;`.trim()
}

function buildCombinedOrderRankSql(activityId, startTime, endTime) {
  return `
SELECT
  e.name AS employee_name,
  e.employee_number,
  COALESCE(eq.employee_used_quota_total, 0) AS employee_used_quota_total,
  COALESCE(fq.friend_used_quota_total, 0) AS friend_used_quota_total,
  COALESCE(eq.employee_used_quota_total, 0) + COALESCE(fq.friend_used_quota_total, 0) AS all_used_quota_total,
  COALESCE(emp_order.order_cnt, 0) AS employee_order_cnt,
  COALESCE(fr_order.order_cnt, 0) AS friend_order_cnt,
  COALESCE(emp_order.order_cnt, 0) + COALESCE(fr_order.order_cnt, 0) AS total_order_cnt,
  COALESCE(emp_order.paid_order_cnt, 0) + COALESCE(fr_order.paid_order_cnt, 0) AS paid_order_cnt,
  COALESCE(emp_order.order_amount, 0) + COALESCE(fr_order.order_amount, 0) AS order_amount,
  COALESCE(emp_order.pay_amount, 0) + COALESCE(fr_order.pay_amount, 0) AS pay_amount,
  COALESCE(emp_order.order_item_qty, 0) + COALESCE(fr_order.order_item_qty, 0) AS order_item_qty,
  COALESCE(emp_order.paid_item_qty, 0) + COALESCE(fr_order.paid_item_qty, 0) AS paid_item_qty
FROM (
  SELECT DISTINCT employee_id
  FROM employee_account
  WHERE employee_activity_id = ${Number(activityId)}
) base
LEFT JOIN employee e ON e.id = base.employee_id
LEFT JOIN (
  SELECT
    em.employee_id,
    COUNT(DISTINCT o.id) AS order_cnt,
    SUM(CASE WHEN o.pay_time IS NOT NULL THEN 1 ELSE 0 END) AS paid_order_cnt,
    SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0)) AS order_amount,
    SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(o.payment, 0) ELSE 0 END) AS pay_amount,
    SUM(COALESCE(oi.total_quantity, 0)) AS order_item_qty,
    SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(oi.total_quantity, 0) ELSE 0 END) AS paid_item_qty
  FROM store_order o
  INNER JOIN (
    SELECT ea2.user_id, ea2.employee_id
    FROM employee_account ea2
    INNER JOIN (
      SELECT user_id, MAX(id) AS max_id
      FROM employee_account
      WHERE employee_activity_id = ${Number(activityId)}
      GROUP BY user_id
    ) pick ON pick.max_id = ea2.id
    WHERE ea2.employee_activity_id = ${Number(activityId)}
  ) em ON em.user_id = o.creator_id
  LEFT JOIN (
    SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
    FROM order_item
    WHERE isdeleted = 0
    GROUP BY order_id
  ) oi ON oi.order_id = o.id
  WHERE o.isdeleted = 0
    AND o.create_time >= '${startTime}'
    AND o.create_time < '${endTime}'
  GROUP BY em.employee_id
) emp_order ON emp_order.employee_id = base.employee_id
LEFT JOIN (
  SELECT
    ea3.employee_id,
    SUM(COALESCE(fa3.used_quota, 0)) AS friend_used_quota_total
  FROM employee_account ea3
  LEFT JOIN friend_account fa3
    ON fa3.employee_account_id = ea3.id
    AND fa3.employee_activity_id = ${Number(activityId)}
  WHERE ea3.employee_activity_id = ${Number(activityId)}
  GROUP BY ea3.employee_id
) fq ON fq.employee_id = base.employee_id
LEFT JOIN (
  SELECT
    employee_id,
    SUM(COALESCE(used_quota, 0)) AS employee_used_quota_total
  FROM employee_account
  WHERE employee_activity_id = ${Number(activityId)}
  GROUP BY employee_id
) eq ON eq.employee_id = base.employee_id
LEFT JOIN (
  SELECT
    fm.owner_employee_id AS employee_id,
    COUNT(DISTINCT o.id) AS order_cnt,
    SUM(CASE WHEN o.pay_time IS NOT NULL THEN 1 ELSE 0 END) AS paid_order_cnt,
    SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0)) AS order_amount,
    SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(o.payment, 0) ELSE 0 END) AS pay_amount,
    SUM(COALESCE(oi.total_quantity, 0)) AS order_item_qty,
    SUM(CASE WHEN o.pay_time IS NOT NULL THEN COALESCE(oi.total_quantity, 0) ELSE 0 END) AS paid_item_qty
  FROM store_order o
  INNER JOIN (
    SELECT
      fa2.user_id,
      ea2.employee_id AS owner_employee_id
    FROM friend_account fa2
    INNER JOIN (
      SELECT user_id, MAX(id) AS max_id
      FROM friend_account
      WHERE employee_activity_id = ${Number(activityId)}
      GROUP BY user_id
    ) pick ON pick.max_id = fa2.id
    LEFT JOIN employee_account ea2 ON ea2.id = fa2.employee_account_id
    WHERE fa2.employee_activity_id = ${Number(activityId)}
  ) fm ON fm.user_id = o.creator_id
  LEFT JOIN (
    SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
    FROM order_item
    WHERE isdeleted = 0
    GROUP BY order_id
  ) oi ON oi.order_id = o.id
  WHERE o.isdeleted = 0
    AND o.create_time >= '${startTime}'
    AND o.create_time < '${endTime}'
  GROUP BY fm.owner_employee_id
) fr_order ON fr_order.employee_id = base.employee_id
WHERE 1 = 1
HAVING total_order_cnt > 0
ORDER BY total_order_cnt DESC
;`.trim()
}

function buildFriendReportSql(activityId, startTime, endTime) {
  return `
SELECT
  e.name AS employee_name,
  e.department_name,
  e.email,
  COALESCE(acc_stat.employee_account_cnt, 0) + COALESCE(fr_issue.friend_issue_cnt, 0) AS account_issue_cnt,
  COALESCE(acc_stat.employee_account_used_cnt, 0) + COALESCE(fr_issue.friend_used_cnt, 0) AS account_used_cnt,
  (
    COALESCE(acc_stat.employee_account_cnt, 0) + COALESCE(fr_issue.friend_issue_cnt, 0)
    - COALESCE(acc_stat.employee_account_used_cnt, 0) - COALESCE(fr_issue.friend_used_cnt, 0)
  ) AS account_unused_cnt,
  COALESCE(emp_amt.employee_order_amount, 0) + COALESCE(fr_amt.friend_order_amount, 0) AS total_purchase_amount
FROM (
  SELECT DISTINCT employee_id
  FROM employee_account
  WHERE employee_activity_id = ${Number(activityId)}
) base
LEFT JOIN employee e ON e.id = base.employee_id
LEFT JOIN (
  SELECT
    ea2.employee_id,
    COUNT(*) AS employee_account_cnt,
    SUM(CASE WHEN COALESCE(ea2.used_quota, 0) > 0 THEN 1 ELSE 0 END) AS employee_account_used_cnt
  FROM employee_account ea2
  WHERE ea2.employee_activity_id = ${Number(activityId)}
  GROUP BY ea2.employee_id
) acc_stat ON acc_stat.employee_id = base.employee_id
LEFT JOIN (
  SELECT
    ea2.employee_id,
    COUNT(fa.id) AS friend_issue_cnt,
    SUM(CASE WHEN COALESCE(fa.used_quota, 0) > 0 THEN 1 ELSE 0 END) AS friend_used_cnt
  FROM employee_account ea2
  LEFT JOIN friend_account fa
    ON fa.employee_account_id = ea2.id
    AND fa.employee_activity_id = ${Number(activityId)}
  WHERE ea2.employee_activity_id = ${Number(activityId)}
  GROUP BY ea2.employee_id
) fr_issue ON fr_issue.employee_id = base.employee_id
LEFT JOIN (
  SELECT
    em.employee_id,
    SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0)) AS employee_order_amount
  FROM store_order o
  INNER JOIN (
    SELECT ea2.user_id, ea2.employee_id
    FROM employee_account ea2
    INNER JOIN (
      SELECT user_id, MAX(id) AS max_id
      FROM employee_account
      WHERE employee_activity_id = ${Number(activityId)}
      GROUP BY user_id
    ) pick ON pick.max_id = ea2.id
    WHERE ea2.employee_activity_id = ${Number(activityId)}
  ) em ON em.user_id = o.creator_id
  WHERE o.isdeleted = 0
    AND o.create_time >= '${startTime}'
    AND o.create_time < '${endTime}'
  GROUP BY em.employee_id
) emp_amt ON emp_amt.employee_id = base.employee_id
LEFT JOIN (
  SELECT
    fm.owner_employee_id AS employee_id,
    SUM(COALESCE(o.actual_pay_amount, 0) + COALESCE(o.shipping_fee, 0)) AS friend_order_amount
  FROM store_order o
  INNER JOIN (
    SELECT
      fa2.user_id,
      ea2.employee_id AS owner_employee_id
    FROM friend_account fa2
    INNER JOIN (
      SELECT user_id, MAX(id) AS max_id
      FROM friend_account
      WHERE employee_activity_id = ${Number(activityId)}
      GROUP BY user_id
    ) pick ON pick.max_id = fa2.id
    LEFT JOIN employee_account ea2 ON ea2.id = fa2.employee_account_id
    WHERE fa2.employee_activity_id = ${Number(activityId)}
  ) fm ON fm.user_id = o.creator_id
  WHERE o.isdeleted = 0
    AND o.create_time >= '${startTime}'
    AND o.create_time < '${endTime}'
  GROUP BY fm.owner_employee_id
) fr_amt ON fr_amt.employee_id = base.employee_id
ORDER BY total_purchase_amount DESC, base.employee_id DESC
;`.trim()
}

function buildEmployeeAccountListSql({ activityId, startTime, endTime, filters }) {
  const whereClauses = [`ea.employee_activity_id = ${Number(activityId)}`]
  if (filters?.employeeName) {
    whereClauses.push(`e.name LIKE '%${escapeSqlText(filters.employeeName)}%'`)
  }
  if (filters?.groupName) {
    whereClauses.push(`eg.group_name LIKE '%${escapeSqlText(filters.groupName)}%'`)
  }
  if (filters?.employeeAccountStatus === 'used') {
    whereClauses.push('COALESCE(ea.used_quota, 0) > 0')
  } else if (filters?.employeeAccountStatus === 'unused') {
    whereClauses.push('COALESCE(ea.used_quota, 0) = 0')
  }
  if (filters?.friendAccountStatus === 'used') {
    whereClauses.push('COALESCE(fa_agg.friend_account_used_cnt, 0) > 0')
  } else if (filters?.friendAccountStatus === 'unused') {
    whereClauses.push('COALESCE(fa_agg.friend_account_used_cnt, 0) = 0')
  }

  return `
SELECT
  ea.id AS employee_account_id,
  ea.user_id,
  ea.used_quota,
  ea.remaining_quota,
  e.id AS employee_id,
  e.name AS employee_name,
  e.employee_number,
  e.department_name,
  eg.group_name,
  COALESCE(oc.order_cnt, 0) AS order_cnt,
  COALESCE(fa_agg.friend_account_cnt, 0) AS friend_account_cnt,
  COALESCE(fa_agg.friend_account_used_cnt, 0) AS friend_account_used_cnt,
  COALESCE(fa_agg.friend_order_cnt_total, 0) AS friend_order_cnt_total,
  COALESCE(fa_agg.friend_used_quota_total, 0) AS friend_used_quota_total
FROM employee_account ea
LEFT JOIN employee e ON e.id = ea.employee_id
LEFT JOIN employee_group eg ON eg.id = e.employee_group_id
LEFT JOIN (
  SELECT
    o.creator_id,
    COUNT(*) AS order_cnt
  FROM store_order o
  WHERE o.isdeleted = 0
    AND o.create_time >= '${startTime}'
    AND o.create_time < '${endTime}'
  GROUP BY o.creator_id
) oc ON oc.creator_id = ea.user_id
LEFT JOIN (
  SELECT
    fa.employee_account_id,
    COUNT(*) AS friend_account_cnt,
    SUM(CASE WHEN COALESCE(fa.used_quota, 0) > 0 THEN 1 ELSE 0 END) AS friend_account_used_cnt,
    SUM(COALESCE(foc.order_cnt, 0)) AS friend_order_cnt_total,
    SUM(COALESCE(fa.used_quota, 0)) AS friend_used_quota_total
  FROM friend_account fa
  LEFT JOIN (
    SELECT
      o.creator_id,
      COUNT(*) AS order_cnt
    FROM store_order o
    WHERE o.isdeleted = 0
      AND o.create_time >= '${startTime}'
      AND o.create_time < '${endTime}'
    GROUP BY o.creator_id
  ) foc ON foc.creator_id = fa.user_id
  WHERE fa.employee_activity_id = ${Number(activityId)}
  GROUP BY fa.employee_account_id
) fa_agg ON fa_agg.employee_account_id = ea.id
WHERE ${whereClauses.join('\n  AND ')}
ORDER BY ea.id DESC
;`.trim()
}

function buildFriendAccountListSql({ activityId, employeeAccountId, startTime, endTime }) {
  return `
SELECT
  fa.id AS friend_account_id,
  fa.user_id,
  fa.used_quota,
  fa.remaining_quota,
  e.name AS friend_name,
  e.employee_number AS friend_employee_number,
  COALESCE(oc.order_cnt, 0) AS order_cnt
FROM friend_account fa
LEFT JOIN employee e ON e.id = fa.employee_id
LEFT JOIN (
  SELECT
    o.creator_id,
    COUNT(*) AS order_cnt
  FROM store_order o
  WHERE o.isdeleted = 0
    AND o.create_time >= '${startTime}'
    AND o.create_time < '${endTime}'
  GROUP BY o.creator_id
) oc ON oc.creator_id = fa.user_id
WHERE fa.employee_activity_id = ${Number(activityId)}
  AND fa.employee_account_id = ${Number(employeeAccountId)}
ORDER BY fa.id DESC
;`.trim()
}

function getAaData(result) {
  return Array.isArray(result?.aaData) ? result.aaData : []
}

function getCurrentHourKey() {
  const now = new Date()
  return `${formatDate(now)} ${pad2(now.getHours())}`
}

function getHourRowClass({ row }) {
  return row?.isCurrentHour ? 'current-hour-row' : ''
}

async function focusCurrentHourRow() {
  await nextTick()
  const table = hourTableRef.value
  if (!table || !table.$el) return
  const currentRow = todayHourlyRows.value.find((item) => item.isCurrentHour)
  if (!currentRow) return
  table.setCurrentRow(currentRow)
  const bodyWrapper = table.$el.querySelector('.el-table__body-wrapper')
  const currentEl = table.$el.querySelector('.el-table__row.current-hour-row')
  if (bodyWrapper && currentEl) {
    bodyWrapper.scrollTop = Math.max(
      0,
      currentEl.offsetTop - bodyWrapper.clientHeight / 2 + currentEl.clientHeight / 2
    )
  }
}

function buildExportRow(row) {
  const payRate = calcRate(row.payUserCnt, row.visitUv)
  const orderRate = calcRate(row.totalOrderCnt, row.visitUv)
  const orderLossRate = calcRate(row.orderUnpaidUserCnt, row.totalOrderCnt)
  const cancelRate = calcRate(row.canceledOrderCnt, row.totalOrderCnt)
  const waitPayLossRate = calcRate(row.waitPayUserCnt, row.payUserCnt + row.waitPayUserCnt)
  return {
    时间: row.bucket,
    访问人数: toNumber(row.visitUv),
    访问次数: toNumber(row.visitPv),
    加购人数: toNumber(row.addUv),
    总订单数: toNumber(row.totalOrderCnt),
    下单人数: toNumber(row.orderUserCnt),
    订单未支付人数: toNumber(row.orderUnpaidUserCnt),
    支付订单数: toNumber(row.paidOrderCnt),
    取消订单数: toNumber(row.canceledOrderCnt),
    取消订单比率: formatPercent(cancelRate),
    下单金额: toNumber(row.orderAmount),
    下单总件数: toNumber(row.orderItemQty),
    支付金额: toNumber(row.payAmount),
    支付总件数: toNumber(row.paidItemQty),
    下单平均金额: toNumber(calcRate(row.orderAmount, row.orderUserCnt)).toFixed(2),
    支付平均金额: toNumber(calcRate(row.payAmount, row.payUserCnt)).toFixed(2),
    支付人数: toNumber(row.payUserCnt),
    待支付人数: toNumber(row.waitPayUserCnt),
    下单转化率: formatPercent(orderRate),
    订单流失率: formatPercent(orderLossRate),
    支付转化率: formatPercent(payRate),
    待支付流失率: formatPercent(waitPayLossRate)
  }
}

function buildTodaySummaryExportRow() {
  const row = {
    bucket: getNowDate(),
    visitPv: toNumber(todayOverview.value.visitPv),
    visitUv: toNumber(todayOverview.value.visitUv),
    addUv: toNumber(todayOverview.value.addUv),
    totalOrderCnt: toNumber(todayOverview.value.totalOrderCnt),
    orderUserCnt: toNumber(todayOverview.value.orderUserCnt),
    orderUnpaidUserCnt: toNumber(todayOverview.value.orderUnpaidUserCnt),
    paidOrderCnt: toNumber(todayOverview.value.paidOrderCnt),
    canceledOrderCnt: toNumber(todayOverview.value.canceledOrderCnt),
    orderAmount: toNumber(todayOverview.value.orderAmount),
    orderItemQty: toNumber(todayOverview.value.orderItemQty),
    payAmount: toNumber(todayOverview.value.payAmount),
    paidItemQty: toNumber(todayOverview.value.paidItemQty),
    payUserCnt: toNumber(todayOverview.value.payUserCnt),
    waitPayUserCnt: toNumber(todayOverview.value.waitPayUserCnt)
  }
  return buildExportRow(row)
}

function exportByRows(rawRows, sheetName, fileNamePrefix) {
  if (!rawRows.length) {
    ElMessage.warning('暂无可导出数据')
    return
  }

  const exportRows = rawRows.map((item) => buildExportRow(item))
  const worksheet = XLSX.utils.json_to_sheet(exportRows)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
  const timestamp = formatDateTime(new Date()).replace(/[-:\s]/g, '')
  XLSX.writeFile(workbook, `${fileNamePrefix}_${timestamp}.xlsx`)
}

function exportHourData() {
  exportByRows(todayHourlyRows.value, '小时数据', 'Burton小时数据')
}

function exportDayData() {
  exportByRows(historyRows.value, '历史数据', 'Burton历史数据')
}

function exportTodaySummaryData() {
  const row = buildTodaySummaryExportRow()
  const worksheet = XLSX.utils.json_to_sheet([row])
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, '今日总榜')
  const timestamp = formatDateTime(new Date()).replace(/[-:\s]/g, '')
  XLSX.writeFile(workbook, `Burton今日总榜_${timestamp}.xlsx`)
}

function buildOrderStatusExportRows() {
  return (todayOrderStatusRows.value || []).map((item) => ({
    订单状态: item.statusLabel,
    状态码: item.status,
    订单数: toNumber(item.count),
    占比: formatPercent(calcRate(item.count, todayOverview.value.orderCnt))
  }))
}

function getTodayReportRows() {
  const map = {}
    ; (historyRows.value || []).forEach((row) => {
      map[row.bucket] = { ...row }
    })
  if (queryDate.value) {
    map[queryDate.value] = {
      bucket: queryDate.value,
      visitPv: toNumber(todayOverview.value.visitPv),
      visitUv: toNumber(todayOverview.value.visitUv),
      addUv: toNumber(todayOverview.value.addUv),
      totalOrderCnt: toNumber(todayOverview.value.totalOrderCnt),
      paidOrderCnt: toNumber(todayOverview.value.paidOrderCnt),
      canceledOrderCnt: toNumber(todayOverview.value.canceledOrderCnt),
      orderAmount: toNumber(todayOverview.value.orderAmount),
      payAmount: toNumber(todayOverview.value.payAmount),
      orderUserCnt: toNumber(todayOverview.value.orderUserCnt),
      payUserCnt: toNumber(todayOverview.value.payUserCnt),
      paidItemQty: toNumber(todayOverview.value.paidItemQty),
      orderItemQty: toNumber(todayOverview.value.orderItemQty)
    }
  }
  return Object.values(map).sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)))
}

function buildTodayReportAoa(rows) {
  const dates = rows.map((row) => row.bucket)
  const formatByType = (value, type) => {
    if (type === 'percent') return formatPercent(value)
    if (type === 'amount') return toNumber(value).toFixed(2)
    return toNumber(value).toFixed(2)
  }
  const metrics = [
    { label: '访问人数', type: 'num', calc: (r) => toNumber(r.visitUv) },
    { label: '访问次数', type: 'num', calc: (r) => toNumber(r.visitPv) },
    { label: '人均PV', type: 'num', calc: (r) => calcRate(r.visitPv, r.visitUv) },
    { label: '加购人数', type: 'num', calc: (r) => toNumber(r.addUv) },
    { label: '加购率', type: 'percent', calc: (r) => calcRate(r.addUv, r.visitUv) },
    { label: '总订单数', type: 'num', calc: (r) => toNumber(r.totalOrderCnt) },
    { label: '支付订单数', type: 'num', calc: (r) => toNumber(r.paidOrderCnt) },
    { label: '取消订单数', type: 'num', calc: (r) => toNumber(r.canceledOrderCnt) },
    { label: '取消订单比率', type: 'percent', calc: (r) => calcRate(r.canceledOrderCnt, r.totalOrderCnt) },
    { label: '总订单金额（下单金额）', type: 'amount', calc: (r) => toNumber(r.orderAmount) },
    { label: '支付金额', type: 'amount', calc: (r) => toNumber(r.payAmount) },
    { label: '下单平均金额', type: 'amount', calc: (r) => calcRate(r.orderAmount, r.orderUserCnt) },
    { label: '支付平均金额（客单价）', type: 'amount', calc: (r) => calcRate(r.payAmount, r.payUserCnt) },
    { label: '支付人数', type: 'num', calc: (r) => toNumber(r.payUserCnt) },
    { label: '支付件数', type: 'num', calc: (r) => toNumber(r.paidItemQty) },
    { label: '件单价APU (支付金额/支付件数)', type: 'amount', calc: (r) => calcRate(r.payAmount, r.paidItemQty) },
    { label: '客单件UPT (支付件数/人数)', type: 'num', calc: (r) => calcRate(r.paidItemQty, r.payUserCnt) },
    { label: '下单转化率（总订单数/访问人数）', type: 'percent', calc: (r) => calcRate(r.totalOrderCnt, r.visitUv) },
    { label: '支付转化率（支付人数/访问人数）', type: 'percent', calc: (r) => calcRate(r.payUserCnt, r.visitUv) },
    { label: 'UV价值', type: 'amount', calc: (r) => calcRate(r.payAmount, r.visitUv) }
  ]
  const aoa = [['指标', ...dates]]
  metrics.forEach((metric) => {
    const row = [metric.label]
    rows.forEach((dataRow) => {
      const val = metric.calc(dataRow)
      if (metric.type === 'num') {
        row.push(toNumber(val))
      } else {
        row.push(formatByType(val, metric.type))
      }
    })
    aoa.push(row)
  })
  return aoa
}

function exportTodayMetricReport() {
  const rows = getTodayReportRows()
  if (!rows.length) {
    ElMessage.warning('暂无可导出报表数据')
    return
  }
  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.aoa_to_sheet(buildTodayReportAoa(rows))
  XLSX.utils.book_append_sheet(workbook, worksheet, '今日报表')
  const timestamp = formatDateTime(new Date()).replace(/[-:\s]/g, '')
  XLSX.writeFile(workbook, `Burton今日报表_${timestamp}.xlsx`)
}

function exportTodaySelectedData() {
  if (!todayExportSelections.value.length) {
    ElMessage.warning('请先勾选要导出的数据')
    return
  }
  const workbook = XLSX.utils.book_new()
  let hasSheet = false
  if (todayExportSelections.value.includes('today-summary')) {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet([buildTodaySummaryExportRow()]),
      '今日总榜'
    )
    hasSheet = true
  }
  if (todayExportSelections.value.includes('today-hour')) {
    const hourRows = todayHourlyRows.value.map((item) => buildExportRow(item))
    if (hourRows.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(hourRows), '今日小时数据')
      hasSheet = true
    }
  }
  if (todayExportSelections.value.includes('today-status')) {
    const statusRows = buildOrderStatusExportRows()
    if (statusRows.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(statusRows), '订单状态分布')
      hasSheet = true
    }
  }
  if (todayExportSelections.value.includes('today-report')) {
    const reportRows = getTodayReportRows()
    if (reportRows.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(buildTodayReportAoa(reportRows)), '今日报表')
      hasSheet = true
    }
  }
  if (!hasSheet) {
    ElMessage.warning('当前勾选项暂无可导出数据')
    return
  }
  const timestamp = formatDateTime(new Date()).replace(/[-:\s]/g, '')
  XLSX.writeFile(workbook, `Burton今日导出_${timestamp}.xlsx`)
}

async function loadEmployeeActivities(config, token) {
  const activityResult = await execBurtonOrderSql(config, token, buildEmployeeActivitySql())
  const rows = getAaData(activityResult).map((item) => ({
    ...item,
    statusText: getEmployeeActivityStatus(item)
  }))
  employeeActivities.value = rows
  if (!rows.length) {
    selectedEmployeeActivityId.value = null
    return null
  }
  const hasSelected = rows.some((item) => Number(item.id) === Number(selectedEmployeeActivityId.value))
  if (!hasSelected) {
    selectedEmployeeActivityId.value = rows[0].id
  }
  return selectedEmployeeActivity.value
}

function getEmployeeActivityRange(activity) {
  const startRaw = parseDateTime(activity?.start_time) || new Date()
  const start = startOfDay(startRaw)
  const endRaw = parseDateTime(activity?.end_time)
  const endExclusive = endRaw ? addDays(startOfDay(endRaw), 1) : addDays(startOfDay(new Date()), 1)
  return {
    startTime: formatDateTime(start),
    endTime: formatDateTime(endExclusive)
  }
}

async function loadEmployeeDashboardData(config, token) {
  const activity = selectedEmployeeActivity.value
  if (!activity) {
    employeeSummary.value = {
      employeeAccountCnt: 0,
      employeeAccountUsedCnt: 0,
      employeeAccountUnusedCnt: 0,
      friendAccountCnt: 0,
      friendAccountUsedCnt: 0,
      friendAccountUnusedCnt: 0,
      employeeOrderCntTotal: 0,
      friendOrderCntTotal: 0,
      allOrderCntTotal: 0,
      employeePaidOrderCntTotal: 0,
      friendPaidOrderCntTotal: 0,
      allPaidOrderCntTotal: 0,
      employeeOrderAmountTotal: 0,
      friendOrderAmountTotal: 0,
      allOrderAmountTotal: 0,
      employeePayAmountTotal: 0,
      friendPayAmountTotal: 0,
      allPayAmountTotal: 0,
      employeeOrderItemQtyTotal: 0,
      friendOrderItemQtyTotal: 0,
      allOrderItemQtyTotal: 0,
      employeePaidItemQtyTotal: 0,
      friendPaidItemQtyTotal: 0,
      allPaidItemQtyTotal: 0,
      employeeUsedQuotaTotal: 0,
      friendUsedQuotaTotal: 0,
      allUsedQuotaTotal: 0
    }
    employeeAccountRows.value = []
    employeeOrderRankRows.value = []
    friendOrderRankRows.value = []
    combinedOrderRankRows.value = []
    employeePage.value.total = 0
    return
  }
  const { startTime, endTime } = getEmployeeActivityRange(activity)
  const summarySql = buildEmployeeSummarySql(activity.id, startTime, endTime)
  const listSql = buildEmployeeAccountListSql({
    activityId: activity.id,
    startTime,
    endTime,
    filters: employeeSearchForm.value
  })

  const pageQuery = {
    iDisplayStart: (employeePage.value.page - 1) * employeePage.value.size,
    iDisplayLength: employeePage.value.size
  }
  const [summaryResult, listResult] = await Promise.all([
    execBurtonOrderSql(config, token, summarySql),
    execBurtonOrderSql(config, token, listSql, pageQuery)
  ])
  const [employeeRankResult, friendRankResult, combinedRankResult] = await Promise.all([
    execBurtonOrderSql(config, token, buildEmployeeOrderRankSql(activity.id, startTime, endTime), { iDisplayStart: 0, iDisplayLength: 5000 }),
    execBurtonOrderSql(config, token, buildFriendOrderRankSql(activity.id, startTime, endTime), { iDisplayStart: 0, iDisplayLength: 5000 }),
    execBurtonOrderSql(config, token, buildCombinedOrderRankSql(activity.id, startTime, endTime), { iDisplayStart: 0, iDisplayLength: 5000 })
  ])
  const summary = getAaData(summaryResult)[0] || {}
  const employeeOrderCntTotal = toNumber(summary.employee_order_cnt_total)
  const friendOrderCntTotal = toNumber(summary.friend_order_cnt_total)
  const employeePaidOrderCntTotal = toNumber(summary.employee_paid_order_cnt_total)
  const friendPaidOrderCntTotal = toNumber(summary.friend_paid_order_cnt_total)
  const employeeOrderAmountTotal = toNumber(summary.employee_order_amount_total)
  const friendOrderAmountTotal = toNumber(summary.friend_order_amount_total)
  const employeePayAmountTotal = toNumber(summary.employee_pay_amount_total)
  const friendPayAmountTotal = toNumber(summary.friend_pay_amount_total)
  const employeeOrderItemQtyTotal = toNumber(summary.employee_order_item_qty_total)
  const friendOrderItemQtyTotal = toNumber(summary.friend_order_item_qty_total)
  const employeePaidItemQtyTotal = toNumber(summary.employee_paid_item_qty_total)
  const friendPaidItemQtyTotal = toNumber(summary.friend_paid_item_qty_total)
  const employeeUsedQuotaTotal = toNumber(summary.employee_used_quota_total)
  const friendUsedQuotaTotal = toNumber(summary.friend_used_quota_total)
  employeeSummary.value = {
    employeeAccountCnt: toNumber(summary.employee_account_cnt),
    employeeAccountUsedCnt: toNumber(summary.employee_account_used_cnt),
    employeeAccountUnusedCnt: toNumber(summary.employee_account_unused_cnt),
    friendAccountCnt: toNumber(summary.friend_account_cnt),
    friendAccountUsedCnt: toNumber(summary.friend_account_used_cnt),
    friendAccountUnusedCnt: toNumber(summary.friend_account_unused_cnt),
    employeeOrderCntTotal,
    friendOrderCntTotal,
    allOrderCntTotal: employeeOrderCntTotal + friendOrderCntTotal,
    employeePaidOrderCntTotal,
    friendPaidOrderCntTotal,
    allPaidOrderCntTotal: employeePaidOrderCntTotal + friendPaidOrderCntTotal,
    employeeOrderAmountTotal,
    friendOrderAmountTotal,
    allOrderAmountTotal: employeeOrderAmountTotal + friendOrderAmountTotal,
    employeePayAmountTotal,
    friendPayAmountTotal,
    allPayAmountTotal: employeePayAmountTotal + friendPayAmountTotal,
    employeeOrderItemQtyTotal,
    friendOrderItemQtyTotal,
    allOrderItemQtyTotal: employeeOrderItemQtyTotal + friendOrderItemQtyTotal,
    employeePaidItemQtyTotal,
    friendPaidItemQtyTotal,
    allPaidItemQtyTotal: employeePaidItemQtyTotal + friendPaidItemQtyTotal,
    employeeUsedQuotaTotal,
    friendUsedQuotaTotal,
    allUsedQuotaTotal: employeeUsedQuotaTotal + friendUsedQuotaTotal
  }

  const listRows = getAaData(listResult).map((item) => ({
    employeeAccountId: toNumber(item.employee_account_id),
    userId: toNumber(item.user_id),
    employeeId: toNumber(item.employee_id),
    employeeName: item.employee_name || '-',
    employeeNumber: item.employee_number || '-',
    departmentName: item.department_name || '-',
    groupName: item.group_name || '-',
    usedQuota: toNumber(item.used_quota),
    remainingQuota: toNumber(item.remaining_quota),
    orderCnt: toNumber(item.order_cnt),
    friendAccountCnt: toNumber(item.friend_account_cnt),
    friendAccountUsedCnt: toNumber(item.friend_account_used_cnt),
    friendOrderCntTotal: toNumber(item.friend_order_cnt_total),
    friendUsedQuotaTotal: toNumber(item.friend_used_quota_total),
    allUsedQuotaTotal: toNumber(item.used_quota) + toNumber(item.friend_used_quota_total),
    accountStatusText: toNumber(item.used_quota) > 0 ? '在用' : '未用',
    friendAccountStatusText: toNumber(item.friend_account_used_cnt) > 0 ? '在用' : '未用',
    friendRows: [],
    friendLoaded: false,
    friendLoading: false
  }))
  employeeAccountRows.value = listRows
  employeePage.value.total = toNumber(listResult?.iTotalDisplayRecords || listResult?.iTotalRecords)
  employeeOrderRankRows.value = getAaData(employeeRankResult).map((item) => ({
    employeeName: item.employee_name || '-',
    employeeNumber: item.employee_number || '-',
    groupName: item.group_name || '-',
    orderCnt: toNumber(item.order_cnt),
    paidOrderCnt: toNumber(item.paid_order_cnt),
    orderAmount: toNumber(item.order_amount),
    payAmount: toNumber(item.pay_amount),
    orderItemQty: toNumber(item.order_item_qty),
    paidItemQty: toNumber(item.paid_item_qty),
    employeeUsedQuotaTotal: toNumber(item.employee_used_quota_total),
    friendUsedQuotaTotal: toNumber(item.friend_used_quota_total),
    allUsedQuotaTotal: toNumber(item.all_used_quota_total)
  }))
  friendOrderRankRows.value = getAaData(friendRankResult).map((item) => ({
    friendName: item.friend_name || '-',
    friendEmployeeNumber: item.friend_employee_number || '-',
    ownerEmployeeName: item.owner_employee_name || '-',
    orderCnt: toNumber(item.order_cnt),
    paidOrderCnt: toNumber(item.paid_order_cnt),
    orderAmount: toNumber(item.order_amount),
    payAmount: toNumber(item.pay_amount),
    orderItemQty: toNumber(item.order_item_qty),
    paidItemQty: toNumber(item.paid_item_qty),
    employeeUsedQuotaTotal: toNumber(item.employee_used_quota_total),
    friendUsedQuotaTotal: toNumber(item.friend_used_quota_total),
    allUsedQuotaTotal: toNumber(item.all_used_quota_total)
  }))
  combinedOrderRankRows.value = getAaData(combinedRankResult).map((item) => ({
    employeeName: item.employee_name || '-',
    employeeNumber: item.employee_number || '-',
    employeeOrderCnt: toNumber(item.employee_order_cnt),
    friendOrderCnt: toNumber(item.friend_order_cnt),
    totalOrderCnt: toNumber(item.total_order_cnt),
    paidOrderCnt: toNumber(item.paid_order_cnt),
    orderAmount: toNumber(item.order_amount),
    payAmount: toNumber(item.pay_amount),
    orderItemQty: toNumber(item.order_item_qty),
    paidItemQty: toNumber(item.paid_item_qty),
    employeeUsedQuotaTotal: toNumber(item.employee_used_quota_total),
    friendUsedQuotaTotal: toNumber(item.friend_used_quota_total),
    allUsedQuotaTotal: toNumber(item.all_used_quota_total)
  }))
}

async function handleEmployeeExpand(row, expandedRows) {
  if (!expandedRows?.some((item) => item.employeeAccountId === row.employeeAccountId)) return
  if (row.friendLoaded || row.friendLoading) return
  const activity = selectedEmployeeActivity.value
  if (!activity) return
  row.friendLoading = true
  try {
    const config = dashboardConfig.value || (await getBurtonStoreConfig())
    dashboardConfig.value = config
    const token = await loginBurtonStore(config)
    const { startTime, endTime } = getEmployeeActivityRange(activity)
    const sql = buildFriendAccountListSql({
      activityId: activity.id,
      employeeAccountId: row.employeeAccountId,
      startTime,
      endTime
    })
    const result = await execBurtonOrderSql(config, token, sql)
    row.friendRows = getAaData(result).map((item) => ({
      friendAccountId: toNumber(item.friend_account_id),
      friendName: item.friend_name || '-',
      friendEmployeeNumber: item.friend_employee_number || '-',
      usedQuota: toNumber(item.used_quota),
      remainingQuota: toNumber(item.remaining_quota),
      orderCnt: toNumber(item.order_cnt),
      friendStatusText: toNumber(item.used_quota) > 0 ? '在用' : '未用'
    }))
    row.friendLoaded = true
  } catch (error) {
    ElMessage.error(error?.message || '加载亲友账号失败')
  } finally {
    row.friendLoading = false
  }
}

function onEmployeeActivityChange() {
  employeePage.value.page = 1
  loadDashboard({ scope: 'employee' })
}

function queryEmployeeAccounts() {
  employeePage.value.page = 1
  loadDashboard({ scope: 'employee' })
}

function resetEmployeeSearch() {
  employeeSearchForm.value = {
    employeeName: '',
    groupName: '',
    employeeAccountStatus: '',
    friendAccountStatus: ''
  }
  employeePage.value.page = 1
  loadDashboard({ scope: 'employee' })
}

function onEmployeePageChange(page) {
  employeePage.value.page = page
  loadDashboard({ scope: 'employee' })
}

function onEmployeeSizeChange(size) {
  employeePage.value.size = size
  employeePage.value.page = 1
  loadDashboard({ scope: 'employee' })
}

async function exportEmployeeDashboardData() {
  const activity = selectedEmployeeActivity.value
  if (!activity) {
    ElMessage.warning('请先选择活动')
    return
  }
  if (!employeeExportSelections.value.length) {
    ElMessage.warning('请先勾选要导出的数据')
    return
  }
  try {
    employeeLoading.value = true
    const config = dashboardConfig.value || (await getBurtonStoreConfig())
    dashboardConfig.value = config
    const token = await loginBurtonStore(config)
    const { startTime, endTime } = getEmployeeActivityRange(activity)

    const employeeSql = buildEmployeeAccountListSql({
      activityId: activity.id,
      startTime,
      endTime,
      filters: employeeSearchForm.value
    })
    const employeeResult = await execBurtonOrderSql(
      config,
      token,
      employeeSql,
      { iDisplayStart: 0, iDisplayLength: 5000 }
    )
    const employeeExportRows = getAaData(employeeResult).map((item) => ({
      员工账号ID: toNumber(item.employee_account_id),
      员工名称: item.employee_name || '-',
      分组名称: item.group_name || '-',
      员工号: item.employee_number || '-',
      部门: item.department_name || '-',
      账号状态: toNumber(item.used_quota) > 0 ? '在用' : '未用',
      使用额度: toNumber(item.used_quota),
      亲友已用额度总额: toNumber(item.friend_used_quota_total),
      员工及亲友已用额度总额: toNumber(item.used_quota) + toNumber(item.friend_used_quota_total),
      剩余额度: toNumber(item.remaining_quota),
      下单数量: toNumber(item.order_cnt),
      亲友账号数: toNumber(item.friend_account_cnt),
      亲友账号在用数: toNumber(item.friend_account_used_cnt),
      亲友账号状态: toNumber(item.friend_account_used_cnt) > 0 ? '在用' : '未用',
      亲友账号下单总数: toNumber(item.friend_order_cnt_total)
    }))

    const friendSql = `
SELECT
  fa.id AS friend_account_id,
  fa.employee_account_id,
  fa.user_id,
  fa.used_quota,
  fa.remaining_quota,
  e.name AS friend_name,
  e.employee_number AS friend_employee_number,
  COALESCE(oc.order_cnt, 0) AS order_cnt
FROM friend_account fa
LEFT JOIN employee e ON e.id = fa.employee_id
LEFT JOIN (
  SELECT
    o.creator_id,
    COUNT(*) AS order_cnt
  FROM store_order o
  WHERE o.isdeleted = 0
    AND o.create_time >= '${startTime}'
    AND o.create_time < '${endTime}'
  GROUP BY o.creator_id
) oc ON oc.creator_id = fa.user_id
WHERE fa.employee_activity_id = ${Number(activity.id)}
ORDER BY fa.id DESC
;`.trim()
    const friendResult = await execBurtonOrderSql(config, token, friendSql, { iDisplayStart: 0, iDisplayLength: 10000 })
    const friendExportRows = getAaData(friendResult).map((item) => ({
      亲友账号ID: toNumber(item.friend_account_id),
      归属员工账号ID: toNumber(item.employee_account_id),
      亲友姓名: item.friend_name || '-',
      亲友员工号: item.friend_employee_number || '-',
      账号状态: toNumber(item.used_quota) > 0 ? '在用' : '未用',
      使用额度: toNumber(item.used_quota),
      剩余额度: toNumber(item.remaining_quota),
      下单数量: toNumber(item.order_cnt)
    }))

    let friendReportRows = []
    if (employeeExportSelections.value.includes('friend-report')) {
      const friendReportResult = await execBurtonOrderSql(
        config,
        token,
        buildFriendReportSql(activity.id, startTime, endTime),
        { iDisplayStart: 0, iDisplayLength: 10000 }
      )
      friendReportRows = getAaData(friendReportResult).map((item) => {
        const issueCnt = toNumber(item.account_issue_cnt)
        const usedCnt = toNumber(item.account_used_cnt)
        return {
          员工名: item.employee_name || '-',
          归属部门: item.department_name || '-',
          个人邮箱: item.email || '-',
          账号发放数量: issueCnt,
          已购买使用账户数量: usedCnt,
          未使用账户数量: toNumber(item.account_unused_cnt),
          使用率: formatPercent(calcRate(usedCnt, issueCnt)),
          累计购买金额: toNumber(item.total_purchase_amount)
        }
      })
    }

    const summaryRows = [{
      活动名称: activity.name || '-',
      活动状态: activity.statusText || '-',
      员工账号数量: toNumber(employeeSummary.value.employeeAccountCnt),
      员工账号在用: toNumber(employeeSummary.value.employeeAccountUsedCnt),
      员工账号未用: toNumber(employeeSummary.value.employeeAccountUnusedCnt),
      亲友账号数量: toNumber(employeeSummary.value.friendAccountCnt),
      亲友账号在用: toNumber(employeeSummary.value.friendAccountUsedCnt),
      亲友账号未用: toNumber(employeeSummary.value.friendAccountUnusedCnt),
      员工下单总数: toNumber(employeeSummary.value.employeeOrderCntTotal),
      亲友下单总数: toNumber(employeeSummary.value.friendOrderCntTotal),
      员工及亲友下单总数: toNumber(employeeSummary.value.allOrderCntTotal),
      员工支付总订单数: toNumber(employeeSummary.value.employeePaidOrderCntTotal),
      亲友支付总订单数: toNumber(employeeSummary.value.friendPaidOrderCntTotal),
      员工及亲友支付总订单数: toNumber(employeeSummary.value.allPaidOrderCntTotal),
      员工下单总金额: toNumber(employeeSummary.value.employeeOrderAmountTotal),
      亲友下单总金额: toNumber(employeeSummary.value.friendOrderAmountTotal),
      员工及亲友下单总金额: toNumber(employeeSummary.value.allOrderAmountTotal),
      员工支付总金额: toNumber(employeeSummary.value.employeePayAmountTotal),
      亲友支付总金额: toNumber(employeeSummary.value.friendPayAmountTotal),
      员工及亲友支付总金额: toNumber(employeeSummary.value.allPayAmountTotal),
      员工下单总件数: toNumber(employeeSummary.value.employeeOrderItemQtyTotal),
      亲友下单总件数: toNumber(employeeSummary.value.friendOrderItemQtyTotal),
      员工及亲友下单总件数: toNumber(employeeSummary.value.allOrderItemQtyTotal),
      员工支付总件数: toNumber(employeeSummary.value.employeePaidItemQtyTotal),
      亲友支付总件数: toNumber(employeeSummary.value.friendPaidItemQtyTotal),
      员工及亲友支付总件数: toNumber(employeeSummary.value.allPaidItemQtyTotal),
      员工额度已用总额: toNumber(employeeSummary.value.employeeUsedQuotaTotal),
      亲友额度已用总额: toNumber(employeeSummary.value.friendUsedQuotaTotal),
      员工及亲友额度已用总额: toNumber(employeeSummary.value.allUsedQuotaTotal)
    }]

    const employeeRankRows = sortedEmployeeOrderRankRows.value
      .map((item, index) => ({
        排名: index + 1,
        员工名称: item.employeeName,
        员工号: item.employeeNumber,
        分组名称: item.groupName,
        下单总订单数: toNumber(item.orderCnt),
        支付总订单数: toNumber(item.paidOrderCnt),
        下单总金额: toNumber(item.orderAmount),
        支付总金额: toNumber(item.payAmount),
        下单总件数: toNumber(item.orderItemQty),
        支付总件数: toNumber(item.paidItemQty),
        亲友额度已用总额: toNumber(item.friendUsedQuotaTotal),
        员工及亲友额度已用总额: toNumber(item.allUsedQuotaTotal)
      }))

    const friendRankRows = sortedFriendOrderRankRows.value
      .map((item, index) => ({
        排名: index + 1,
        亲友名称: item.friendName,
        亲友员工号: item.friendEmployeeNumber,
        归属员工: item.ownerEmployeeName,
        下单总订单数: toNumber(item.orderCnt),
        支付总订单数: toNumber(item.paidOrderCnt),
        下单总金额: toNumber(item.orderAmount),
        支付总金额: toNumber(item.payAmount),
        下单总件数: toNumber(item.orderItemQty),
        支付总件数: toNumber(item.paidItemQty),
        员工额度已用总额: toNumber(item.employeeUsedQuotaTotal),
        亲友额度已用总额: toNumber(item.friendUsedQuotaTotal),
        员工及亲友额度已用总额: toNumber(item.allUsedQuotaTotal)
      }))

    const combinedRankRows = sortedCombinedOrderRankRows.value
      .map((item, index) => ({
        排名: index + 1,
        员工名称: item.employeeName,
        员工号: item.employeeNumber,
        员工下单总订单数: toNumber(item.employeeOrderCnt),
        亲友下单总订单数: toNumber(item.friendOrderCnt),
        下单总订单数: toNumber(item.totalOrderCnt),
        支付总订单数: toNumber(item.paidOrderCnt),
        下单总金额: toNumber(item.orderAmount),
        支付总金额: toNumber(item.payAmount),
        下单总件数: toNumber(item.orderItemQty),
        支付总件数: toNumber(item.paidItemQty)
      }))

    const workbook = XLSX.utils.book_new()
    let hasSheet = false
    if (employeeExportSelections.value.includes('summary')) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), '汇总统计')
      hasSheet = true
    }
    if (employeeExportSelections.value.includes('employee-list')) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(employeeExportRows), '员工账号')
      hasSheet = true
    }
    if (employeeExportSelections.value.includes('friend-list')) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(friendExportRows), '亲友账号')
      hasSheet = true
    }
    if (employeeExportSelections.value.includes('friend-report')) {
      const friendReportHeaders = ['员工名', '归属部门', '个人邮箱', '账号发放数量(含员工本身)', '已购买使用账户数量(含员工本身)', '未使用账户数量(含员工本身)', '使用率', '累计购买金额']
      const friendReportSheet = friendReportRows.length
        ? XLSX.utils.json_to_sheet(friendReportRows)
        : XLSX.utils.aoa_to_sheet([friendReportHeaders])
      XLSX.utils.book_append_sheet(
        workbook,
        friendReportSheet,
        '亲友账号报表'
      )
      hasSheet = true
    }
    if (employeeExportSelections.value.includes('employee-rank') && employeeRankRows.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(employeeRankRows), '员工下单排行')
      hasSheet = true
    }
    if (employeeExportSelections.value.includes('friend-rank') && friendRankRows.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(friendRankRows), '亲友下单排行')
      hasSheet = true
    }
    if (employeeExportSelections.value.includes('combined-rank') && combinedRankRows.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(combinedRankRows), '员工及亲友排行')
      hasSheet = true
    }
    if (!hasSheet) {
      ElMessage.warning('当前勾选项暂无可导出数据')
      return
    }
    const timestamp = formatDateTime(new Date()).replace(/[-:\s]/g, '')
    XLSX.writeFile(workbook, `Burton员工看板导出_${timestamp}.xlsx`)
  } catch (error) {
    ElMessage.error(error?.message || '导出员工看板数据失败')
  } finally {
    employeeLoading.value = false
  }
}

function resetAutoRefreshCountdown() {
  autoRefreshSecondsLeft.value = AUTO_REFRESH_SECONDS
}

function startAutoRefreshTimer() {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
  countdownTimer = setInterval(() => {
    if (autoRefreshSecondsLeft.value > 0) {
      autoRefreshSecondsLeft.value -= 1
      return
    }
    if (loading.value) return
    autoRefreshSecondsLeft.value = AUTO_REFRESH_SECONDS
    const scope = activeTab.value === 'employee' ? 'employee' : (activeTab.value === 'history' ? 'history' : 'today')
    loadDashboard({ source: 'auto', scope })
  }, 1000)
}

function stopAutoRefreshTimer() {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function setupResizeObserverWarningGuard() {
  resizeObserverErrorHandler = (event) => {
    const message = event?.message || ''
    if (message.includes('ResizeObserver loop completed with undelivered notifications')) {
      event.stopImmediatePropagation?.()
    }
  }
  window.addEventListener('error', resizeObserverErrorHandler)
}

function manualRefresh() {
  const scope = activeTab.value === 'employee' ? 'employee' : (activeTab.value === 'history' ? 'history' : 'today')
  loadDashboard({ source: 'manual', scope })
}

function applyHistoryRangeReload() {
  if (!historyRange.value || historyRange.value.length !== 2) {
    ElMessage.warning('请选择历史开始和结束日期')
    return
  }
  loadDashboard({ source: 'manual', scope: 'history' })
}

function getRangeIntersection(startA, endA, startB, endB) {
  const start = startA > startB ? startA : startB
  const end = endA < endB ? endA : endB
  if (start >= end) return null
  return { start, end }
}

async function loadDashboard(options = {}) {
  const scope = options.scope || 'all'
  const shouldLoadToday = scope === 'all' || scope === 'today'
  const shouldLoadHistory = scope === 'all' || scope === 'history'
  const shouldLoadEmployee = scope === 'all' || scope === 'employee'
  const requestId = ++latestRequestId
  if (shouldLoadEmployee) {
    employeeLoading.value = true
  }
  loading.value = true
  try {
    const now = new Date()
    const todayStart = startOfDay(now)
    const tomorrowStart = addDays(todayStart, 1)
    const launchDay = parseDateStart(launchDate.value) || todayStart
    const selectedQueryDate = shouldLoadToday
      ? (parseDateStart(queryDate.value) || todayStart)
      : todayStart
    const queryDayStart = selectedQueryDate
    const queryDayEnd = addDays(queryDayStart, 1)
    const isQueryToday = formatDate(queryDayStart) === formatDate(todayStart)
    const queryUseTodayTable = shouldUseTrackingTodayTable(queryDayStart, now)

    const { historyStart, historyEndExclusive, hasHistory } = normalizeHistoryRange(
      launchDay,
      todayStart
    )
    if (shouldLoadHistory) {
      historyWindow.value = {
        start: hasHistory ? formatDate(historyStart) : '-',
        end: hasHistory ? formatDate(addDays(historyEndExclusive, -1)) : '-'
      }
    }

    const trackingTodayTotalPromise = shouldLoadToday
      ? fetchBurtonTracking(
        buildTrackingParams({
          start: queryDayStart,
          end: queryDayEnd,
          formatTime: 'yyyy-MM-dd',
          table: queryUseTodayTable
            ? 'mf_kafka_tracking_today'
            : 'mf_tracking_data_origin_record'
        })
      )
      : Promise.resolve(null)
    const trackingTodayHourPromise = shouldLoadToday
      ? fetchBurtonTracking(
        buildTrackingParams({
          start: queryDayStart,
          end: queryDayEnd,
          formatTime: 'yyyy-MM-dd HH',
          table: queryUseTodayTable
            ? 'mf_kafka_tracking_today'
            : 'mf_tracking_data_origin_record'
        })
      )
      : Promise.resolve(null)
    const historyPartOrigin = shouldLoadHistory
      ? getRangeIntersection(historyStart, historyEndExclusive, launchDay, todayStart)
      : null
    const historyPartToday = shouldLoadHistory
      ? getRangeIntersection(historyStart, historyEndExclusive, todayStart, tomorrowStart)
      : null
    const trackingHistoryOriginPromise = historyPartOrigin
      ? fetchBurtonTracking(
        buildTrackingParams({
          start: historyPartOrigin.start,
          end: historyPartOrigin.end,
          formatTime: 'yyyy-MM-dd',
          table: 'mf_tracking_data_origin_record'
        })
      )
      : Promise.resolve([])
    const trackingHistoryTodayPromise = historyPartToday
      ? fetchBurtonTracking(
        buildTrackingParams({
          start: historyPartToday.start,
          end: historyPartToday.end,
          formatTime: 'yyyy-MM-dd',
          table: 'mf_kafka_tracking_today'
        })
      )
      : Promise.resolve([])

    const [
      trackingTodayTotalRows,
      trackingTodayHourRows,
      trackingHistoryOriginRows,
      trackingHistoryTodayRows
    ] = await Promise.all([
      trackingTodayTotalPromise,
      trackingTodayHourPromise,
      trackingHistoryOriginPromise,
      trackingHistoryTodayPromise
    ])
    const trackingHistoryRows = [
      ...(Array.isArray(trackingHistoryOriginRows) ? trackingHistoryOriginRows : []),
      ...(Array.isArray(trackingHistoryTodayRows) ? trackingHistoryTodayRows : [])
    ]

    const config = dashboardConfig.value || (await getBurtonStoreConfig())
    dashboardConfig.value = config
    const token = await loginBurtonStore(config)

    if (shouldLoadEmployee) {
      await loadEmployeeActivities(config, token)
      await loadEmployeeDashboardData(config, token)
    }

    const orderTodaySummaryPromise = shouldLoadToday
      ? execBurtonOrderSql(config, token, buildOrderSummarySql(queryDayStart, queryDayEnd))
      : Promise.resolve(null)
    const prevDayStart = addDays(queryDayStart, -1)
    const prevDayEnd = queryDayStart
    const orderPrevSummaryPromise = shouldLoadToday
      ? execBurtonOrderSql(config, token, buildOrderSummarySql(prevDayStart, prevDayEnd))
      : Promise.resolve(null)
    const trackingPrevTotalPromise = shouldLoadToday
      ? fetchBurtonTracking(
        buildTrackingParams({
          start: prevDayStart,
          end: prevDayEnd,
          formatTime: 'yyyy-MM-dd',
          table: shouldUseTrackingTodayTable(prevDayStart, now)
            ? 'mf_kafka_tracking_today'
            : 'mf_tracking_data_origin_record'
        })
      )
      : Promise.resolve([])
    const orderTodayHourPromise = shouldLoadToday
      ? execBurtonOrderSql(config, token, buildOrderGroupSql(queryDayStart, queryDayEnd, 'hour'))
      : Promise.resolve(null)
    const orderTodayStatusPromise = shouldLoadToday
      ? execBurtonOrderSql(config, token, buildOrderStatusSql(queryDayStart, queryDayEnd))
      : Promise.resolve(null)
    const orderHistoryDailyPromise = shouldLoadHistory && hasHistory
      ? execBurtonOrderSql(
        config,
        token,
        buildOrderGroupSql(historyStart, historyEndExclusive, 'day')
      )
      : Promise.resolve({ aaData: [] })

    const [orderTodaySummary, orderTodayHour, orderTodayStatus, orderHistoryDaily, orderPrevSummary, trackingPrevTotalRows] =
      await Promise.all([
        orderTodaySummaryPromise,
        orderTodayHourPromise,
        orderTodayStatusPromise,
        orderHistoryDailyPromise,
        orderPrevSummaryPromise,
        trackingPrevTotalPromise
      ])

    if (requestId !== latestRequestId) return

    const trackingHistoryDayMap = shouldLoadHistory
      ? groupTrackingRows(trackingHistoryRows, 'day')
      : {}

    if (shouldLoadToday) {
      const todayDayKey = formatDate(queryDayStart)
      const currentHourKey = getCurrentHourKey()
      const trackingTodayDayMap = groupTrackingRows(trackingTodayTotalRows, 'day')
      const trackingTodayHourMap = groupTrackingRows(trackingTodayHourRows, 'hour')

      const todayTrack = trackingTodayDayMap[todayDayKey] || {
        visitPv: 0,
        visitUv: 0,
        addPv: 0,
        addUv: 0
      }
      const todayOrderSummaryRow = getAaData(orderTodaySummary)[0] || {}
      const prevTrackMap = groupTrackingRows(trackingPrevTotalRows, 'day')
      const prevTrack = prevTrackMap[formatDate(prevDayStart)] || {}
      const prevOrderSummaryRow = getAaData(orderPrevSummary)[0] || {}

      todayOverview.value = {
        visitPv: toNumber(todayTrack.visitPv),
        visitUv: toNumber(todayTrack.visitUv),
        addPv: toNumber(todayTrack.addPv),
        addUv: toNumber(todayTrack.addUv),
        totalOrderCnt: toNumber(todayOrderSummaryRow.total_order_cnt),
        paidOrderCnt: toNumber(todayOrderSummaryRow.paid_order_cnt),
        canceledOrderCnt: toNumber(todayOrderSummaryRow.canceled_order_cnt),
        orderUnpaidUserCnt: toNumber(todayOrderSummaryRow.order_unpaid_user_cnt),
        payUserCnt: toNumber(todayOrderSummaryRow.pay_user_cnt),
        waitPayUserCnt: toNumber(todayOrderSummaryRow.wait_pay_user_cnt),
        orderUserCnt: toNumber(todayOrderSummaryRow.order_user_cnt),
        orderCnt: toNumber(todayOrderSummaryRow.total_order_cnt),
        orderAmount: toNumber(todayOrderSummaryRow.order_amount),
        payAmount: toNumber(todayOrderSummaryRow.pay_amount),
        orderItemQty: toNumber(todayOrderSummaryRow.order_item_qty),
        paidItemQty: toNumber(todayOrderSummaryRow.paid_item_qty)
      }
      const currentPayRate = calcRate(todayOverview.value.payUserCnt, todayOverview.value.visitUv)
      const prevPayRate = calcRate(toNumber(prevOrderSummaryRow.pay_user_cnt), toNumber(prevTrack.visitUv))
      const currentOrderRate = calcRate(todayOverview.value.totalOrderCnt, todayOverview.value.visitUv)
      const prevOrderRate = calcRate(toNumber(prevOrderSummaryRow.total_order_cnt), toNumber(prevTrack.visitUv))
      const currentOrderLossRate = calcRate(todayOverview.value.orderUnpaidUserCnt, todayOverview.value.totalOrderCnt)
      const prevOrderLossRate = calcRate(
        toNumber(prevOrderSummaryRow.order_unpaid_user_cnt),
        toNumber(prevOrderSummaryRow.total_order_cnt)
      )
      const currentWaitLoss = calcRate(
        todayOverview.value.waitPayUserCnt,
        todayOverview.value.payUserCnt + todayOverview.value.waitPayUserCnt
      )
      const prevWaitLoss = calcRate(
        toNumber(prevOrderSummaryRow.wait_pay_user_cnt),
        toNumber(prevOrderSummaryRow.pay_user_cnt) + toNumber(prevOrderSummaryRow.wait_pay_user_cnt)
      )
      todayOverviewMom.value = {
        visitPvMom: calcMomRate(todayOverview.value.visitPv, toNumber(prevTrack.visitPv)),
        visitUvMom: calcMomRate(todayOverview.value.visitUv, toNumber(prevTrack.visitUv)),
        addUvMom: calcMomRate(todayOverview.value.addUv, toNumber(prevTrack.addUv)),
        totalOrderCntMom: calcMomRate(
          todayOverview.value.totalOrderCnt,
          toNumber(prevOrderSummaryRow.total_order_cnt)
        ),
        orderUserCntMom: calcMomRate(
          todayOverview.value.orderUserCnt,
          toNumber(prevOrderSummaryRow.order_user_cnt)
        ),
        orderUnpaidUserCntMom: calcMomRate(
          todayOverview.value.orderUnpaidUserCnt,
          toNumber(prevOrderSummaryRow.order_unpaid_user_cnt)
        ),
        paidOrderCntMom: calcMomRate(
          todayOverview.value.paidOrderCnt,
          toNumber(prevOrderSummaryRow.paid_order_cnt)
        ),
        payUserCntMom: calcMomRate(todayOverview.value.payUserCnt, toNumber(prevOrderSummaryRow.pay_user_cnt)),
        waitPayUserCntMom: calcMomRate(
          todayOverview.value.waitPayUserCnt,
          toNumber(prevOrderSummaryRow.wait_pay_user_cnt)
        ),
        orderRateMom: calcMomRate(currentOrderRate, prevOrderRate),
        orderLossRateMom: calcMomRate(currentOrderLossRate, prevOrderLossRate),
        payRateMom: calcMomRate(currentPayRate, prevPayRate),
        waitPayLossRateMom: calcMomRate(currentWaitLoss, prevWaitLoss),
        orderAmountMom: calcMomRate(todayOverview.value.orderAmount, toNumber(prevOrderSummaryRow.order_amount)),
        orderItemQtyMom: calcMomRate(todayOverview.value.orderItemQty, toNumber(prevOrderSummaryRow.order_item_qty)),
        payAmountMom: calcMomRate(todayOverview.value.payAmount, toNumber(prevOrderSummaryRow.pay_amount)),
        paidItemQtyMom: calcMomRate(todayOverview.value.paidItemQty, toNumber(prevOrderSummaryRow.paid_item_qty)),
        avgOrderAmountMom: calcMomRate(
          calcRate(todayOverview.value.orderAmount, todayOverview.value.orderUserCnt),
          calcRate(toNumber(prevOrderSummaryRow.order_amount), toNumber(prevOrderSummaryRow.order_user_cnt))
        ),
        avgPayAmountMom: calcMomRate(
          calcRate(todayOverview.value.payAmount, todayOverview.value.payUserCnt),
          calcRate(toNumber(prevOrderSummaryRow.pay_amount), toNumber(prevOrderSummaryRow.pay_user_cnt))
        )
      }

      const hourKeys = getDateRangeKeys(queryDayStart, queryDayEnd, 'hour')
      const orderHourMap = rowArrayToMap(getAaData(orderTodayHour), 'hour_bucket')
      todayHourlyRows.value = hourKeys.map((hourKey) => {
        const track = trackingTodayHourMap[hourKey] || {}
        const order = orderHourMap[hourKey] || {}
        return {
          hourKey,
          bucket: `${hourKey.slice(11, 13)}:00`,
          visitPv: toNumber(track.visitPv),
          visitUv: toNumber(track.visitUv),
          addPv: toNumber(track.addPv),
          addUv: toNumber(track.addUv),
          totalOrderCnt: toNumber(order.total_order_cnt),
          canceledOrderCnt: toNumber(order.canceled_order_cnt),
          orderUserCnt: toNumber(order.order_user_cnt),
          orderUnpaidUserCnt: toNumber(order.order_unpaid_user_cnt),
          paidOrderCnt: toNumber(order.paid_order_cnt),
          payUserCnt: toNumber(order.pay_user_cnt),
          waitPayUserCnt: toNumber(order.wait_pay_user_cnt),
          orderCnt: toNumber(order.total_order_cnt),
          orderAmount: toNumber(order.order_amount),
          orderItemQty: toNumber(order.order_item_qty),
          avgOrderAmount: calcRate(toNumber(order.order_amount), toNumber(order.order_user_cnt)),
          payAmount: toNumber(order.pay_amount),
          paidItemQty: toNumber(order.paid_item_qty),
          avgPayAmount: calcRate(toNumber(order.pay_amount), toNumber(order.pay_user_cnt)),
          isCurrentHour: isQueryToday && hourKey === currentHourKey
        }
      })
      todayHourlyRows.value = enrichMomRows(todayHourlyRows.value)

      const statusMap = rowArrayToMap(getAaData(orderTodayStatus), 'order_status')
      todayOrderStatusRows.value = STATUS_ORDER.map((status) => {
        const row = statusMap[status] || {}
        return {
          status,
          statusLabel: STATUS_LABEL_MAP[status] || status,
          count: toNumber(row.status_cnt)
        }
      })
    }

    if (shouldLoadHistory) {
      const historyKeys = hasHistory
        ? getDateRangeKeys(historyStart, historyEndExclusive, 'day')
        : []
      const orderDayMap = rowArrayToMap(getAaData(orderHistoryDaily), 'day_bucket')
      const historyAscRows = historyKeys.map((dayKey) => {
        const track = trackingHistoryDayMap[dayKey] || {}
        const order = orderDayMap[dayKey] || {}
        return {
          bucket: dayKey,
          visitPv: toNumber(track.visitPv),
          visitUv: toNumber(track.visitUv),
          addPv: toNumber(track.addPv),
          addUv: toNumber(track.addUv),
          totalOrderCnt: toNumber(order.total_order_cnt),
          canceledOrderCnt: toNumber(order.canceled_order_cnt),
          orderUserCnt: toNumber(order.order_user_cnt),
          orderUnpaidUserCnt: toNumber(order.order_unpaid_user_cnt),
          paidOrderCnt: toNumber(order.paid_order_cnt),
          payUserCnt: toNumber(order.pay_user_cnt),
          waitPayUserCnt: toNumber(order.wait_pay_user_cnt),
          orderCnt: toNumber(order.total_order_cnt),
          orderAmount: toNumber(order.order_amount),
          orderItemQty: toNumber(order.order_item_qty),
          avgOrderAmount: calcRate(toNumber(order.order_amount), toNumber(order.order_user_cnt)),
          payAmount: toNumber(order.pay_amount),
          paidItemQty: toNumber(order.paid_item_qty),
          avgPayAmount: calcRate(toNumber(order.pay_amount), toNumber(order.pay_user_cnt))
        }
      })
      historyRows.value = enrichMomRows(historyAscRows).reverse()
    }

    await nextTick()
    if (shouldLoadToday) {
      renderTodayCharts()
    }
    if (shouldLoadHistory) {
      renderHistoryChart()
    }
    lastRefreshTime.value = formatDateTime(new Date())
    resetAutoRefreshCountdown()
    if (shouldLoadToday && isQueryToday) {
      await focusCurrentHourRow()
    }
  } catch (error) {
    console.error('加载Burton看板失败:', error)
    ElMessage.error(error?.message || '加载看板失败，请稍后重试')
  } finally {
    if (requestId === latestRequestId) {
      loading.value = false
    }
    if (shouldLoadEmployee) {
      employeeLoading.value = false
    }
    resetAutoRefreshCountdown()
  }
}

watch(
  () => launchDate.value,
  (value) => {
    if (!value) return
    localStorage.setItem(STORAGE_KEY, value)
    if (suppressLaunchWatch.value) return
    historyRange.value = getDefaultHistoryRange(value)
    const scope = activeTab.value === 'employee' ? 'employee' : (activeTab.value === 'history' ? 'history' : 'today')
    loadDashboard({ scope })
  }
)

watch(
  () => queryDate.value,
  (value) => {
    if (!value) return
    if (suppressLaunchWatch.value) return
    if (activeTab.value === 'today') {
      loadDashboard({ scope: 'today' })
    }
  }
)

watch(
  () => todayTrendMetric.value,
  () => {
    nextTick(() => {
      renderTodayCharts()
    })
  }
)

watch(
  () => historyTrendMetric.value,
  () => {
    nextTick(() => {
      renderHistoryChart()
    })
  }
)

watch(
  () => activeTab.value,
  (value) => {
    nextTick(() => {
      if (value === 'today') {
        renderTodayCharts()
      } else if (value === 'history') {
        renderHistoryChart()
      } else if (value === 'employee') {
        loadDashboard({ scope: 'employee' })
      }
      resizeAllCharts()
    })
  }
)

onMounted(async () => {
  try {
    const config = await getBurtonStoreConfig()
    dashboardConfig.value = config
    const dictDate = parseProjectStartDate(config)
    const cachedDate = localStorage.getItem(STORAGE_KEY)
    launchDate.value = dictDate || cachedDate || getNowDate()
    queryDate.value = getNowDate()
    historyRange.value = getDefaultHistoryRange(launchDate.value)
  } catch (error) {
    launchDate.value = localStorage.getItem(STORAGE_KEY) || getNowDate()
    queryDate.value = getNowDate()
    historyRange.value = getDefaultHistoryRange(launchDate.value)
  } finally {
    await nextTick()
    suppressLaunchWatch.value = false
    setupResizeObserverWarningGuard()
    window.addEventListener('resize', handleWindowResize)
    startAutoRefreshTimer()
    resetAutoRefreshCountdown()
    await loadDashboard()
  }
})

onBeforeUnmount(() => {
  stopAutoRefreshTimer()
  window.removeEventListener('resize', handleWindowResize)
  if (chartResizeTimer) {
    clearTimeout(chartResizeTimer)
    chartResizeTimer = null
  }
  disposeChart(todayTrendChart)
  disposeChart(todayStatusChart)
  disposeChart(historyTrendChart)
  todayTrendChart = null
  todayStatusChart = null
  historyTrendChart = null
  if (resizeObserverErrorHandler) {
    window.removeEventListener('error', resizeObserverErrorHandler)
    resizeObserverErrorHandler = null
  }
})
</script>

<style scoped>
.burton-dashboard {
  --bd-bg-a: #eff6ff;
  --bd-bg-b: #f5fdf8;
  --bd-card: #ffffff;
  --bd-text: #132031;
  --bd-subtext: #5b6b80;
  --bd-border: #d9e8f7;

  min-height: 100vh;
  padding: 24px;
  color: var(--bd-text);
  background:
    radial-gradient(circle at 12% 8%, rgba(24, 119, 242, 0.2), transparent 28%),
    radial-gradient(circle at 90% 10%, rgba(14, 166, 106, 0.18), transparent 30%),
    linear-gradient(145deg, var(--bd-bg-a) 0%, var(--bd-bg-b) 48%, #fff9ef 100%);
}

.hero-card {
  margin-bottom: 20px;
  border: 1px solid var(--bd-border);
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.94), rgba(248, 252, 255, 0.92));
  box-shadow: 0 16px 40px rgba(19, 32, 49, 0.07);
  backdrop-filter: blur(4px);
}

.hero-content {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 22px 14px;
}

.hero-content h1 {
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.hero-content p {
  margin: 8px 0 0;
  color: var(--bd-subtext);
}

.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.countdown-text {
  font-size: 13px;
  color: #48617b;
  background: rgba(24, 119, 242, 0.08);
  border: 1px solid rgba(24, 119, 242, 0.22);
  border-radius: 999px;
  padding: 6px 10px;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 22px 18px;
  color: #48617b;
  font-size: 13px;
}

.dashboard-tabs :deep(.el-tabs__header) {
  margin-bottom: 14px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.kpi-card {
  border: 1px solid var(--bd-border);
  border-radius: 14px;
  padding: 14px;
  background: var(--bd-card);
  box-shadow: 0 10px 20px rgba(19, 32, 49, 0.05);
}

.kpi-label {
  margin: 0;
  color: #4f6178;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.kpi-value {
  margin: 10px 0 6px;
  font-size: 28px;
  font-weight: 700;
  color: #112235;
}

.kpi-desc {
  margin: 0;
  color: #6b7d93;
  font-size: 12px;
}

.kpi-mom {
  margin: 6px 0 4px;
  font-size: 12px;
  font-weight: 600;
}

.metric-help {
  color: #7f95af;
  cursor: pointer;
  font-size: 14px;
}

.metric-head {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.mom-text {
  font-weight: 600;
}

.metric-with-mom {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.2;
}

.is-up {
  color: #d14343;
}

.is-down {
  color: #1f9d55;
}

.is-flat {
  color: #718096;
}

.data-grid {
  display: grid;
  grid-template-columns: 2fr 1.1fr;
  gap: 16px;
}

.chart-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.panel-card {
  border: 1px solid var(--bd-border);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
}

.history-trend-card {
  margin-top: 16px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-weight: 600;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.export-multi-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.trend-select {
  width: 180px;
}

.export-btn {
  border-radius: 999px;
}

.panel-tip {
  color: #6e839d;
  font-size: 12px;
  font-weight: 400;
}

.employee-activity-select {
  width: 360px;
}

.employee-activity-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
  color: #48617b;
  font-size: 13px;
}

.employee-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}

.employee-list-card {
  margin-top: 16px;
}

.employee-rank-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.search-input {
  width: 180px;
}

.search-select {
  width: 140px;
}

.rank-select {
  width: 170px;
}

.employee-pagination {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}

.friend-table-wrap {
  padding: 8px 12px 12px;
  background: #f8fbff;
  border-radius: 10px;
}

.echart-box {
  width: 100%;
  height: 300px;
  background: linear-gradient(180deg, rgba(24, 119, 242, 0.04), rgba(14, 166, 106, 0.04));
  border-radius: 10px;
}

:deep(.el-table th .cell) {
  white-space: nowrap;
}

:deep(.hourly-table .el-table__row.current-hour-row) {
  background: rgba(24, 119, 242, 0.12) !important;
}

@media (max-width: 1220px) {
  .data-grid {
    grid-template-columns: 1fr;
  }

  .chart-grid {
    grid-template-columns: 1fr;
  }

  .employee-rank-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .burton-dashboard {
    padding: 12px;
  }

  .hero-content {
    flex-direction: column;
  }

  .panel-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
</style>
